import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { toLocalYMD } from '../lib/dates';
import { createPortal } from 'react-dom';
import { 
  Search, Phone, Mail, MessageSquare, MoreVertical,
  Plus, Filter, ChevronRight, MapPin, Users, Upload,
  RotateCcw, X, User, CreditCard, Bell, Building2,
  FileText, Paperclip, Calendar, Clock, CheckCircle2,
  RefreshCw, ListChecks, ClipboardList, ChevronLeft,
  ExternalLink, Trash2, Edit3, SkipForward, Briefcase,
  Check, CheckCheck, PoundSterling, Edit2, Link2, Copy, Loader2, Banknote, AlertTriangle,
  Square, CheckSquare, UserPlus, Send
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { csvJobs, zoneColors, assigneeColors } from '../data/csvData';
import * as api from '../services/api';
import { safeOpenUrl, openGoogleMapsSearch, openGoogleMapsNavigation } from '../utils/navigation';
import { subscribeToRefresh, customerChanged, Actions } from '../utils/dataSync';
import { cacheCustomers, getCachedCustomers, isOnline } from '../utils/offlineCache';
import { persistCustomService } from '../utils/persistCustomService';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import NewCustomerForm from '../components/NewCustomerForm';
import CustomerImportSection from '../components/CustomerImportSection';
import JobEditModal from '../components/JobEditModal';
import JobDetailPanel from '../components/JobDetailPanel';
import InvoiceSummaryModal from '../components/InvoiceSummaryModal';
import MiniMapPreview from '../components/MiniMapPreview';
import CSVImport from '../components/CSVImport';
import AddressPickerModal from '../components/AddressPickerModal';
import DaySuggestions from '../components/DaySuggestions';
import BulkUnitAdder from '../components/BulkUnitAdder';
import TakePaymentModal from '../components/TakePaymentModal';
import SMSComposeModal from '../components/SMSComposeModal';
import NotifyCustomersModal from '../components/NotifyCustomersModal';
import EmailComposeModal from '../components/EmailComposeModal';
import WhatsAppComposeModal from '../components/WhatsAppComposeModal';
import CustomerPhotos from '../components/CustomerPhotos';
import CustomerNotesEditor from '../components/CustomerNotesEditor';

// Avatar colors for initials
const avatarColors = [
  '#3b82f6', '#8b5cf6', '#ec4899', '#f97316', '#10b981',
  '#14b8a6', '#06b6d4', '#6366f1', '#84cc16', '#f59e0b',
  '#ef4444', '#a855f7', '#0ea5e9', '#22c55e', '#e11d48'
];

const getAvatarColor = (name) => {
  if (!name) return avatarColors[0];
  const charCode = name.charCodeAt(0) + (name.charCodeAt(1) || 0);
  return avatarColors[charCode % avatarColors.length];
};

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.split(' ').filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const getZoneColor = (zone) => {
  return zoneColors[zone] || zoneColors['default'];
};

const getAssigneeColor = (assignee) => {
  return assigneeColors[assignee] || assigneeColors['default'];
};

const formatDateNice = (dateStr) => {
  if (!dateStr) return 'Not set';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: '2-digit' });
  } catch {
    return dateStr;
  }
};

// Integration Badge Component - Rectangle Style
const IntegrationBadge = ({ type }) => {
  if (type === 'stripe') {
    return (
      <div className="px-2.5 py-1 rounded-md flex items-center gap-1.5 bg-[#635BFF]" title="Stripe connected">
        <span className="text-white text-[11px] font-bold tracking-wide">STRIPE</span>
      </div>
    );
  }
  if (type === 'xero') {
    return (
      <div className="px-2.5 py-1 rounded-md bg-[#13B5EA]" title="Xero connected">
        <span className="text-white text-[11px] font-bold tracking-wide">XERO</span>
      </div>
    );
  }
  if (type === 'gocardless') {
    return (
      <div className="px-2.5 py-1 rounded-md flex items-center gap-1.5 bg-[#1E3264]" title="GoCardless connected">
        <span className="text-white text-[11px] font-bold tracking-wide">GC</span>
      </div>
    );
  }
  return null;
};

// Customer Row Component
const formatNextDue = (dateStr) => {
  const [y, m, d] = String(dateStr).slice(0, 10).split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
};

const CustomerRow = ({ customer, onClick, isSelected, onToggleSelect, selectionMode, onDateClick }) => {
  // Use real customer data - has_stripe is true only if they have a saved payment method
  const hasStripe = customer.has_stripe || false;
  const hasGoCardless = customer.has_gocardless || customer.gocardless_customer_id || false;
  const balance = customer.balance || 0;
  const owes = balance < 0;
  const isInactive = customer.is_active === false;
  
  const handleCheckboxClick = (e) => {
    e.stopPropagation();
    onToggleSelect && onToggleSelect(customer.id);
  };
  
  return (
    <div 
      data-testid={`customer-row-${customer.id}`}
      onClick={onClick}
      className={`relative bg-white rounded-xl border p-4 mb-3 mx-4 hover:shadow-md transition-all duration-200 cursor-pointer ${
        isInactive ? 'opacity-60 bg-zinc-50' : ''
      } ${isSelected ? 'border-emerald-500 bg-emerald-50/30' : 'border-zinc-100 hover:border-zinc-200'}`}
    >
      {customer.next_due && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDateClick && onDateClick(String(customer.next_due).slice(0, 10));
          }}
          className="absolute bottom-3 left-[72%] -translate-x-1/2 flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100 hover:bg-emerald-100 transition-colors"
          title="Open this day in the Work Planner"
          data-testid={`customer-next-due-${customer.id}`}
        >
          <Calendar size={11} />
          {formatNextDue(customer.next_due)}
        </button>
      )}
      <div className="flex items-start gap-3">
        {/* Checkbox - always visible */}
        <button 
          onClick={handleCheckboxClick}
          className={`flex-shrink-0 mt-1 p-0.5 rounded transition-colors ${
            isSelected ? 'text-emerald-600' : 'text-zinc-300 hover:text-zinc-500'
          }`}
        >
          {isSelected ? (
            <CheckSquare size={22} className="fill-emerald-100" />
          ) : (
            <Square size={22} />
          )}
        </button>
        
        <div 
          className={`w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold shadow-md flex-shrink-0 ${isInactive ? 'grayscale' : ''}`}
          style={{ backgroundColor: getAvatarColor(customer.name) }}
        >
          {getInitials(customer.name)}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-zinc-900">{customer.name}</h3>
            {isInactive && (
              <span className="px-2 py-0.5 rounded-md text-xs font-bold bg-zinc-200 text-zinc-600 uppercase tracking-wide">
                Inactive
              </span>
            )}
          </div>
          <p className="text-sm text-zinc-500 truncate mt-0.5">{customer.address}</p>
          
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {customer.zone && (
              <span 
                className="px-2.5 py-1 rounded-lg text-xs font-medium text-white"
                style={{ backgroundColor: getZoneColor(customer.zone) }}
              >
                {customer.zone}
              </span>
            )}
            {customer.primary_assignee && (
              <span className="px-2.5 py-1 rounded-lg text-xs font-medium bg-purple-100 text-purple-700 flex items-center gap-1">
                <User size={10} />
                {customer.primary_assignee.split(',')[0]}
              </span>
            )}
            {hasStripe && (
              <span className="px-2.5 py-1 rounded-lg text-xs font-medium bg-purple-100 text-purple-700">
                Stripe
              </span>
            )}
            {hasGoCardless && (
              <span className="px-2.5 py-1 rounded-lg text-xs font-medium bg-cyan-100 text-cyan-700">
                GoCardless
              </span>
            )}
            {owes && (
              <span className="px-2.5 py-1 rounded-lg text-xs font-medium bg-red-100 text-red-700">
                Owes £{Math.abs(balance).toFixed(2)}
              </span>
            )}
          </div>
        </div>
        
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-1.5">
            {hasStripe && <IntegrationBadge type="stripe" />}
            {hasGoCardless && <IntegrationBadge type="gocardless" />}
          </div>
          
          <div className="flex items-center gap-1 bg-zinc-100 rounded-lg p-1">
            <button className="p-1.5 hover:bg-white rounded-lg transition-colors" onClick={e => e.stopPropagation()}>
              <MessageSquare size={14} className="text-zinc-500" />
            </button>
            <button className="p-1.5 hover:bg-white rounded-lg transition-colors" onClick={e => e.stopPropagation()}>
              <Mail size={14} className="text-zinc-500" />
            </button>
            <button className="p-1.5 hover:bg-white rounded-lg transition-colors" onClick={e => e.stopPropagation()}>
              <Phone size={14} className="text-zinc-500" />
            </button>
          </div>
          
          <div className="text-right">
            <p className={`text-xl font-bold ${customer.balance < 0 ? 'text-red-500' : 'text-zinc-900'}`}>
              £{Math.abs(customer.balance || 0).toFixed(2)}
            </p>
            {customer.zone && <p className="text-xs text-zinc-400">{customer.zone}</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

// Clickable Row for detail modal
const ClickableRow = ({ icon: Icon, children, onClick, rightContent, className = '' }) => (
  <button 
    onClick={onClick}
    className={`w-full p-4 flex items-center gap-3 hover:bg-gray-50 active:bg-gray-100 transition-colors text-left border-b border-gray-100 ${className}`}
  >
    <Icon size={20} className="text-gray-400 flex-shrink-0" />
    <div className="flex-1 min-w-0">{children}</div>
    {rightContent}
    <ChevronRight size={20} className="text-emerald-500 flex-shrink-0" />
  </button>
);

// Job Card in Customer Detail
const CustomerJobCard = ({ job, onClick, onNavigateToDate, onEditJob, onDeleteJob, onOpenVisit, onRepeatJob }) => {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = React.useRef(null);
  
  // Close menu when clicking/touching outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    
    if (showMenu) {
      // Small delay to prevent immediate close on mobile
      const timer = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside, { passive: true });
      }, 100);
      
      return () => {
        clearTimeout(timer);
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showMenu]);
  
  // Use actual job data from API
  const serviceType = job.service_type || job.services || job.title || 'Window clean';
  const frequency = job.frequency || '';
  const lastDone = job.last_done || job.lastDone;
  const nextDue = job.next_due || job.nextDue || job.scheduled_date;
  const status = job.status || job.state || 'scheduled';
  const zone = job.zone || job.round;
  
  // Calculate time difference for display
  const getTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const weeks = Math.floor(diffDays / 7);
    const days = diffDays % 7;
    if (weeks > 0) return `${weeks} week${weeks > 1 ? 's' : ''}${days > 0 ? `, ${days} day${days > 1 ? 's' : ''}` : ''} ago`;
    return `${days} day${days > 1 ? 's' : ''} ago`;
  };
  
  const getTimeUntil = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffTime = date - now;
    if (diffTime < 0) return 'overdue';
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const weeks = Math.floor(diffDays / 7);
    const days = diffDays % 7;
    if (weeks > 0) return `in ${weeks} week${weeks > 1 ? 's' : ''}${days > 0 ? `, ${days} day${days > 1 ? 's' : ''}` : ''}`;
    return `in ${days} day${days > 1 ? 's' : ''}`;
  };

  // Handle clicking on a date to navigate to Work Planner
  const handleDateClick = (dateStr, e) => {
    e.stopPropagation();
    if (onNavigateToDate && dateStr) {
      onNavigateToDate(dateStr);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm mx-4 mb-4 overflow-hidden">
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="font-medium text-gray-900">
              {serviceType}
            </h3>
            {frequency && (
              <p className="text-xs text-gray-500 mt-1">{frequency}</p>
            )}
            {zone && (
              <span 
                className="inline-block mt-2 px-2 py-0.5 rounded text-xs font-medium text-white"
                style={{ backgroundColor: getZoneColor(zone) }}
              >
                {zone}
              </span>
            )}
            {job.assignee && (
              <span className="inline-block mt-2 ml-1 px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                <User size={10} className="inline mr-1" />
                {job.assignee}
              </span>
            )}
          </div>
          <div className="text-right relative" ref={menuRef}>
            <button 
              onClick={(e) => { 
                e.stopPropagation(); 
                e.preventDefault();
                setShowMenu(prev => !prev); 
              }}
              className="p-1 hover:bg-gray-100 rounded"
              data-testid="job-menu-btn"
            >
              <MoreVertical size={16} className="text-gray-400" />
            </button>
            
            {/* Dropdown Menu */}
            {showMenu && (
              <div className="absolute right-0 top-8 w-48 bg-white rounded-xl shadow-2xl z-50 overflow-hidden border border-zinc-200">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setShowMenu(false);
                    // Small delay to ensure menu closes before modal opens
                    setTimeout(() => {
                      if (onEditJob) onEditJob(job);
                    }, 50);
                  }}
                  className="w-full px-4 py-3 flex items-center gap-3 text-left text-zinc-700 hover:bg-zinc-50 transition-colors"
                  data-testid="edit-job-btn"
                >
                  <Edit3 size={16} className="text-zinc-400" />
                  <span className="text-sm">Edit Job</span>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setShowMenu(false);
                    if (onNavigateToDate && nextDue) onNavigateToDate(nextDue);
                  }}
                  className="w-full px-4 py-3 flex items-center gap-3 text-left text-zinc-700 hover:bg-zinc-50 transition-colors"
                >
                  <Calendar size={16} className="text-zinc-400" />
                  <span className="text-sm">View in Planner</span>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setShowMenu(false);
                    if (onDeleteJob) onDeleteJob(job);
                  }}
                  className="w-full px-4 py-3 flex items-center gap-3 text-left text-red-600 hover:bg-red-50 transition-colors border-t border-zinc-100"
                >
                  <Trash2 size={16} />
                  <span className="text-sm">Delete Job</span>
                </button>
              </div>
            )}
            
            <p className="text-xl font-light mt-1">£{(job.price || 0).toFixed(2)}</p>
            <p className="text-xs text-gray-400">PRICE</p>
          </div>
        </div>
        
        {job.notes && (
          <div className="mt-3 p-2 bg-gray-50 rounded">
            <p className="text-xs font-medium text-gray-500">Notes</p>
            <p className="text-sm text-gray-700">{job.notes}</p>
          </div>
        )}
        
        {/* Job History/Schedule - Clickable dates */}
        <div className="mt-3 space-y-2">
          {/* Last Done */}
          {lastDone && (
            <button 
              onClick={(e) => { e.stopPropagation(); if (onOpenVisit) onOpenVisit(job, lastDone, true); }}
              className="w-full flex items-center gap-3 p-2 hover:bg-purple-50 rounded text-left transition-colors group"
              title="View this visit"
              data-testid="job-card-last-done"
            >
              <div className="w-8 h-8 bg-purple-100 rounded flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                <CheckCircle2 size={16} className="text-purple-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-sm">Last Done</p>
                <p className="text-xs text-gray-500 group-hover:text-purple-600 transition-colors">{formatDateNice(lastDone)} ({getTimeAgo(lastDone)})</p>
              </div>
              <Calendar size={14} className="text-gray-300 group-hover:text-purple-500 transition-colors" />
              <ChevronRight size={16} className="text-gray-400 group-hover:text-purple-500 transition-colors" />
            </button>
          )}
          
          {/* Next Due / Scheduled Date */}
          {nextDue && (
            <button 
              onClick={(e) => { e.stopPropagation(); if (onOpenVisit) onOpenVisit(job, nextDue, false); }}
              className="w-full flex items-center gap-3 p-2 hover:bg-emerald-50 rounded text-left transition-colors group"
              title="View this visit"
              data-testid="job-card-next-due"
            >
              <div className={`w-8 h-8 rounded flex items-center justify-center transition-colors ${status === 'completed' ? 'bg-green-100 group-hover:bg-green-200' : 'bg-blue-100 group-hover:bg-emerald-200'}`}>
                <Calendar size={16} className={status === 'completed' ? 'text-green-600' : 'text-emerald-600'} />
              </div>
              <div className="flex-1">
                <p className="font-medium text-sm">{status === 'completed' ? 'Completed' : status === 'scheduled' ? 'Next Due' : status}</p>
                <p className="text-xs text-gray-500 group-hover:text-emerald-600 transition-colors">{formatDateNice(nextDue)} ({status === 'completed' ? 'done' : getTimeUntil(nextDue)})</p>
              </div>
              <Calendar size={14} className="text-gray-300 group-hover:text-emerald-500 transition-colors" />
              <ChevronRight size={16} className="text-gray-400 group-hover:text-emerald-500 transition-colors" />
            </button>
          )}
        </div>
        
        {/* Action Buttons */}
        <div className="grid grid-cols-3 gap-2 mt-4">
          <Button variant="outline" className="w-full px-1 text-xs sm:text-sm" onClick={onClick}>
            APPOINTMENTS
          </Button>
          <Button
            variant="outline"
            className="w-full px-1 text-xs sm:text-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid="repeat-job-btn"
            onClick={(e) => {
              e.stopPropagation();
              if (onRepeatJob) onRepeatJob(job);
            }}
          >
            REPEAT JOB
          </Button>
          <Button 
            variant="outline" 
            className="w-full px-1 text-xs sm:text-sm"
            onClick={(e) => {
              e.stopPropagation();
              if (onEditJob) onEditJob(job);
            }}
          >
            EDIT JOB
          </Button>
        </div>
      </div>
    </div>
  );
};

// Job Detail Modal (when clicking from customer's jobs)
const JobDetailModal = ({ job, customer, onClose, onEditJob }) => {
  const [showToast, setShowToast] = useState('');
  const [historyIndex, setHistoryIndex] = useState(0);
  const [showMenu, setShowMenu] = useState(false);
  
  // Swipe gesture state
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  
  const minSwipeDistance = 100;
  
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  
  const handleTouchMove = (e) => {
    const currentTouch = e.targetTouches[0].clientX;
    setTouchEnd(currentTouch);
    if (touchStart !== null) {
      const offset = currentTouch - touchStart;
      if (offset > 0) setSwipeOffset(offset);
    }
  };
  
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) {
      setSwipeOffset(0);
      return;
    }
    if (touchEnd - touchStart > minSwipeDistance) {
      onClose();
    }
    setSwipeOffset(0);
    setTouchStart(null);
    setTouchEnd(null);
  };
  
  if (!job) return null;
  
  const showMessage = (msg) => {
    setShowToast(msg);
    setTimeout(() => setShowToast(''), 2000);
  };
  
  const handleOpenMaps = () => {
    openGoogleMapsSearch(customer?.address || job.address);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex justify-end" onClick={onClose}>
      <div 
        className="w-full max-w-md bg-white h-full overflow-y-auto" 
        onClick={e => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          transform: `translateX(${swipeOffset}px)`,
          transition: swipeOffset === 0 ? 'transform 0.3s ease-out' : 'none',
          opacity: swipeOffset > 0 ? Math.max(0.3, 1 - swipeOffset / 300) : 1,
          paddingTop: 'env(safe-area-inset-top, 0px)'
        }}
      >
        {showToast && (
          <div className="fixed left-1/2 transform -translate-x-1/2 bg-gray-800 text-white px-4 py-2 rounded-lg shadow-lg z-50" style={{ top: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}>
            {showToast}
          </div>
        )}
        
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-4">
          <div className="flex items-center justify-between mb-3">
            <button onClick={onClose} className="text-white/80 hover:text-white flex items-center gap-1 p-2 -ml-2 hover:bg-white/10 rounded">
              <ChevronLeft size={20} />
              <span>Back</span>
            </button>
            <div className="relative">
              <button 
                onClick={() => setShowMenu(!showMenu)}
                className="p-2 hover:bg-white/10 rounded"
              >
                <MoreVertical size={20} />
              </button>
              
              {/* Dropdown Menu */}
              {showMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
                  <div className="absolute right-0 top-12 w-48 bg-white rounded-xl shadow-2xl z-50 overflow-hidden border border-zinc-200">
                    <button
                      onClick={() => {
                        setShowMenu(false);
                        if (onEditJob) {
                          onClose();
                          onEditJob(job);
                        }
                      }}
                      className="w-full px-4 py-3 flex items-center gap-3 text-left text-zinc-700 hover:bg-zinc-50 transition-colors"
                    >
                      <Edit3 size={16} className="text-zinc-400" />
                      <span className="text-sm">Edit Job</span>
                    </button>
                    <button
                      onClick={() => {
                        setShowMenu(false);
                        showMessage('Reschedule coming soon...');
                      }}
                      className="w-full px-4 py-3 flex items-center gap-3 text-left text-zinc-700 hover:bg-zinc-50 transition-colors"
                    >
                      <Calendar size={16} className="text-zinc-400" />
                      <span className="text-sm">Reschedule</span>
                    </button>
                    <button
                      onClick={() => {
                        setShowMenu(false);
                        showMessage('Skip job coming soon...');
                      }}
                      className="w-full px-4 py-3 flex items-center gap-3 text-left text-zinc-700 hover:bg-zinc-50 transition-colors border-t border-zinc-100"
                    >
                      <SkipForward size={16} className="text-amber-500" />
                      <span className="text-sm">Skip Job</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
          
          <h2 className="text-lg font-medium truncate">{customer?.address?.split(',')[0] || job.address?.split(',')[0]}</h2>
          
          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center">
                <Calendar size={24} />
              </div>
              <div>
                <p className="text-sm">{formatDateNice(job.nextDue)}</p>
                <p className="text-white/70 text-sm">Due &gt; In 3 Weeks, 4 Days</p>
                <p className="text-white/50 text-xs">{customer?.id}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-light">£{(job.price || 0).toFixed(2)}</p>
              <p className="text-white/50 text-xs">JOB PRICE</p>
            </div>
          </div>
          
          {/* Quick actions and tags */}
          <div className="flex items-center gap-2 mt-4">
            <button onClick={() => showMessage('Opening calendar...')} className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-white/20">
              <Calendar size={18} />
            </button>
            <button onClick={() => showMessage('Opening invoice...')} className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-white/20">
              <FileText size={18} />
            </button>
            <button onClick={() => showMessage('Opening payment...')} className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-white/20">
              <CreditCard size={18} />
            </button>
            <button onClick={() => showMessage('Opening notes...')} className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-white/20">
              <ClipboardList size={18} />
            </button>
            
            <div className="flex-1 flex flex-wrap gap-1.5 ml-2">
              {job.assignee && (
                <span className="px-2 py-1 bg-white/20 rounded text-xs font-medium uppercase">
                  {job.assignee.split(',')[0]}
                </span>
              )}
              <span className="px-2 py-1 bg-white/20 rounded text-xs font-medium">IN 25 DAYS</span>
              {job.zone && <span className="px-2 py-1 bg-white/20 rounded text-xs font-medium">{job.zone}</span>}
            </div>
          </div>
          
          <div className="mt-2">
            <span className="px-2 py-1 bg-white/20 rounded text-xs font-medium uppercase">
              {job.service || job.services || 'Window Clean'}
            </span>
          </div>
        </div>
        
        {/* Map */}
        <MiniMapPreview
          latitude={customer?.latitude}
          longitude={customer?.longitude}
          address={customer?.address}
          height="192px"
          showNavigate={true}
          onClick={handleOpenMaps}
        />
        
        {/* Content */}
        <div className="divide-y divide-gray-100">
          <button onClick={handleOpenMaps} className="w-full p-4 flex items-start gap-3 hover:bg-gray-50 text-left">
            <MapPin size={20} className="text-emerald-500 mt-0.5 flex-shrink-0" />
            <span className="flex-1 text-emerald-600">{customer?.address}</span>
          </button>
          
          <ClickableRow icon={MapPin} onClick={() => showMessage('Opening map pin editor...')}>
            <span>Change Map Pin Location</span>
          </ClickableRow>
          
          {/* History navigation */}
          <div className="p-4 flex items-center">
            <button onClick={() => setHistoryIndex(Math.max(-3, historyIndex - 1))} disabled={historyIndex <= -3} className={`p-2 rounded hover:bg-gray-100 ${historyIndex <= -3 ? 'text-gray-300' : 'text-gray-600'}`}>
              <ChevronLeft size={24} />
            </button>
            <div className="flex-1 grid grid-cols-2 text-center gap-2">
              <div className="bg-gray-50 rounded-lg p-2">
                <p className="text-xs text-gray-500">2 Weeks, 1 Day Ago</p>
                <p className="font-medium text-sm">Fri, 16th Jan 26</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-2">
                <p className="text-xs text-gray-500">In 9 Weeks, 4 Days</p>
                <p className="font-medium text-sm text-emerald-600">Wed, 8th Apr 26</p>
              </div>
            </div>
            <button onClick={() => setHistoryIndex(Math.min(3, historyIndex + 1))} disabled={historyIndex >= 3} className={`p-2 rounded hover:bg-gray-100 ${historyIndex >= 3 ? 'text-gray-300' : 'text-emerald-500'}`}>
              <ChevronRight size={24} />
            </button>
          </div>
          
          <ClickableRow icon={User} onClick={() => showMessage(`Opening ${customer?.name}'s profile...`)} rightContent={
            <div className="text-right">
              <span className="text-xl font-light">£{(customer?.balance || 0).toFixed(2)}</span>
              <span className="text-xs text-gray-500 block">BALANCE</span>
            </div>
          }>
            <span className="font-medium">{customer?.name}</span>
          </ClickableRow>
          
          {customer?.phone && (
            <button onClick={() => window.open(`tel:${customer.phone}`, '_self')} className="w-full p-4 flex items-center gap-3 hover:bg-gray-50 text-left border-b border-gray-100">
              <Phone size={20} className="text-green-500" />
              <span className="text-emerald-600 font-medium">{customer.phone}</span>
            </button>
          )}
          
          <ClickableRow icon={MessageSquare} onClick={() => showMessage('Opening message composer...')}>
            <span>Send message using default notification method</span>
          </ClickableRow>
          
          <ClickableRow icon={Calendar} onClick={() => showMessage('Opening date picker...')} rightContent={
            <div className="text-right">
              <span className="text-gray-600">{formatDateNice(job.nextDue)}</span>
              <span className="text-xs text-gray-400 block">in 3 weeks, 4 days</span>
            </div>
          }>
            <span>Due</span>
          </ClickableRow>
          
          <ClickableRow icon={Clock} onClick={() => showMessage('Opening time picker...')} rightContent={<span className="text-gray-600">Any</span>}>
            <span>Start Time</span>
          </ClickableRow>
          
          <ClickableRow icon={Clock} onClick={() => showMessage('Opening duration editor...')} rightContent={<span className="text-gray-600">{job.duration || '00:30'}</span>}>
            <span>Estimated Duration</span>
          </ClickableRow>
          
          <ClickableRow icon={ClipboardList} onClick={() => showMessage('Opening status selector...')} rightContent={
            <span className="px-2 py-1 bg-emerald-600 text-white text-xs rounded">Future Appointment</span>
          }>
            <span>Status</span>
          </ClickableRow>
          
          <ClickableRow icon={ListChecks} onClick={() => showMessage('Opening services editor...')} rightContent={
            <span className="text-gray-600 text-sm truncate max-w-[120px]">{job.services || 'Window Clean'}</span>
          }>
            <span>Services</span>
          </ClickableRow>
          
          <ClickableRow icon={CreditCard} onClick={() => showMessage('Opening price editor...')} rightContent={
            <div className="text-right">
              <span className="text-xl font-light">£{(job.price || 0).toFixed(2)}</span>
              <span className="text-xs text-gray-500 block">JOB PRICE</span>
            </div>
          }>
            <span>Price</span>
          </ClickableRow>
          
          {job.notes && (
            <ClickableRow icon={ClipboardList} onClick={() => showMessage('Opening notes editor...')} className="bg-yellow-50">
              <div>
                <p className="text-sm text-gray-700">{job.notes}</p>
              </div>
            </ClickableRow>
          )}
          
          <ClickableRow icon={Paperclip} onClick={() => showMessage('Opening attachments...')}>
            <div>
              <p>Appointment Attachments</p>
              <p className="text-sm text-gray-500">This Appointment has no attachments.</p>
            </div>
          </ClickableRow>
          
          <ClickableRow icon={Paperclip} onClick={() => showMessage('Opening job attachments...')}>
            <div>
              <p>Job Attachments</p>
              <p className="text-sm text-gray-500">This Job has no attachments.</p>
            </div>
          </ClickableRow>
          
          {/* Assignees */}
          <div className="p-4">
            <div className="flex items-center gap-3 mb-2">
              <Users size={20} className="text-gray-400" />
              <span className="flex-1 font-medium">Assignees</span>
              <Plus size={20} className="text-emerald-500" />
            </div>
            {job.assignee && job.assignee.split(',').map((a, i) => (
              <button key={i} onClick={() => showMessage(`Opening ${a.trim()}'s profile...`)} className="flex items-center gap-3 ml-8 py-2 w-full hover:bg-gray-50">
                <User size={18} className="text-gray-400" />
                <span className="flex-1">{a.trim()}</span>
                <ChevronRight size={16} className="text-emerald-500" />
              </button>
            ))}
          </div>
          
          <ClickableRow icon={Users} onClick={() => showMessage('Opening skills editor...')}>
            <span>Required Skills</span>
          </ClickableRow>
          
          <ClickableRow icon={RefreshCw} onClick={() => showMessage('Opening round selector...')} rightContent={<span className="text-gray-600">{job.zone || 'Not assigned'}</span>}>
            <span>Round</span>
          </ClickableRow>
          
          <ClickableRow icon={RefreshCw} onClick={() => showMessage('Opening frequency editor...')}>
            <span>{job.frequency || 'Ad hoc'}</span>
          </ClickableRow>
        </div>
        
        {/* FAB - Positioned on LEFT to avoid overlapping with content */}
        <button className="fixed bottom-6 left-6 w-14 h-14 bg-emerald-500 rounded-full shadow-lg flex items-center justify-center text-white">
          <MoreVertical size={24} />
        </button>
      </div>
    </div>
  );
};

// Customer Detail Modal
const CustomerDetailModal = ({ customer, onClose, onNavigateToDate, onEditCustomer, onDeleteCustomer, onStatusChange, autoOpenAddJob = false }) => {
  const [activeTab, setActiveTab] = useState('customer');
  const [showToast, setShowToast] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);
  const [invoiceForModal, setInvoiceForModal] = useState(null);
  const [customerJobs, setCustomerJobs] = useState([]);
  const [allCustomerJobs, setAllCustomerJobs] = useState([]);
  const [jobsRefreshTick, setJobsRefreshTick] = useState(0);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [showAddJobModal, setShowAddJobModal] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [showFabMenu, setShowFabMenu] = useState(false);
  const [showNotesPanel, setShowNotesPanel] = useState(false);
  const [showTakePayment, setShowTakePayment] = useState(false);
  const [localBalance, setLocalBalance] = useState(customer?.balance || 0);
  // Mirror customer.notes locally so the editor stays in sync across multiple
  // tabs (Finance + JobDetailPanel) within the same session.
  const [localNotes, setLocalNotes] = useState(customer?.notes || '');
  useEffect(() => { setLocalNotes(customer?.notes || ''); }, [customer?.notes]);
  const [customerLocation, setCustomerLocation] = useState({
    lat: customer?.latitude || customer?.geolocation?.lat,
    lng: customer?.longitude || customer?.geolocation?.lng
  });
  
  // SMS state
  const [showSMSCompose, setShowSMSCompose] = useState(false);
  const [showEmailCompose, setShowEmailCompose] = useState(false);
  const [showWaCompose, setShowWaCompose] = useState(false);

  // Finance history state
  const [financeHistory, setFinanceHistory] = useState([]);
  const [loadingFinance, setLoadingFinance] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [sendingInvoice, setSendingInvoice] = useState(false);

  // Memoised WhatsApp recipient — prevents new object refs on every render
  // which were causing unnecessary re-renders of the modal (and felt like
  // "the app keeps refreshing" to the user).
  // NOTE: must be declared AFTER financeHistory state (TDZ otherwise crashes the component).
  const waRecipient = useMemo(() => {
    const lastUnpaid = (financeHistory || [])
      .flatMap(g => g.entries || [])
      .find(e => e.type === 'invoice' && e.status !== 'paid');
    return {
      id: customer?.id,
      customer_id: customer?.id,
      name: customer?.name,
      phone: customer?.phone,
      balance: localBalance,
      last_invoice_id: lastUnpaid?.invoice_id || lastUnpaid?.id,
      last_invoice_amount: lastUnpaid?.total ?? lastUnpaid?.amount,
    };
  }, [customer?.id, customer?.name, customer?.phone, localBalance, financeHistory]);
  
  // Combined messages (SMS + Email) state
  const [combinedMessages, setCombinedMessages] = useState([]);
  const [loadingCombinedMessages, setLoadingCombinedMessages] = useState(false);

  // Per-invoice "send in flight" guard so double-clicks can't fire twice.
  // Map of { [invoiceEntryId]: true } while a resend is in progress.
  const [sendingInvoiceIds, setSendingInvoiceIds] = useState({});
  
  // Job times state
  const [jobTimes, setJobTimes] = useState([]);
  const [loadingJobTimes, setLoadingJobTimes] = useState(false);
  
  // Sync customerLocation when customer prop changes (for live UI updates)
  useEffect(() => {
    if (customer) {
      setCustomerLocation({
        lat: customer.latitude || customer.geolocation?.lat,
        lng: customer.longitude || customer.geolocation?.lng
      });
    }
  }, [customer?.latitude, customer?.longitude, customer?.geolocation?.lat, customer?.geolocation?.lng]);
  
  // Swipe gesture state
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  
  const minSwipeDistance = 100;
  
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  
  const handleTouchMove = (e) => {
    const currentTouch = e.targetTouches[0].clientX;
    setTouchEnd(currentTouch);
    if (touchStart !== null) {
      const offset = currentTouch - touchStart;
      if (offset > 0) setSwipeOffset(offset);
    }
  };
  
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) {
      setSwipeOffset(0);
      return;
    }
    if (touchEnd - touchStart > minSwipeDistance) {
      onClose();
    }
    setSwipeOffset(0);
    setTouchStart(null);
    setTouchEnd(null);
  };
  
  // Services
  const [availableServices, setAvailableServices] = useState([]);
  
  // Add Job form state
  const [newJobService, setNewJobService] = useState('');
  const [newJobPrice, setNewJobPrice] = useState('');
  const [newJobFrequency, setNewJobFrequency] = useState('6');
  const [newJobDate, setNewJobDate] = useState('');
  const [newJobAddress, setNewJobAddress] = useState('');
  // New (May 2026): customer-facing invoice notes + Squeegee-style pricing
  // mode. Mirrors the fields available on NewJobModal & JobEditModal so all
  // three job entry points share the same data shape on save.
  const [newJobInvoiceNotes, setNewJobInvoiceNotes] = useState('');
  const [newJobPricingMode, setNewJobPricingMode] = useState('flat'); // 'flat' | 'lines'
  const [newJobLineItems, setNewJobLineItems] = useState([]);
  const [savingJob, setSavingJob] = useState(false);
  const [jobError, setJobError] = useState('');
  
  // Fetch services
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const token = localStorage.getItem('streakfree_token');
        const res = await fetch(`${API_URL}/api/services`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setAvailableServices(data);
          // Don't prefill — user must explicitly pick a service via "+ Add a service".
        }
      } catch (err) {
        console.error('Error fetching services:', err);
      }
    };
    fetchServices();
  }, []);
  
  // Initialize job address when modal opens
  useEffect(() => {
    if (showAddJobModal && customer?.address) {
      setNewJobAddress(customer.address);
      // Set default price from service
      const selectedSvc = availableServices.find(s => s.name === newJobService);
      if (selectedSvc?.default_price && !newJobPrice) {
        setNewJobPrice(selectedSvc.default_price.toString());
      }
    }
  }, [showAddJobModal, customer?.address, availableServices, newJobService]);

  // Auto-open the Add Job modal when the panel mounts for a brand-new
  // customer (parent passed autoOpenAddJob=true after the user clicked
  // "Yes" on the post-add prompt).
  useEffect(() => {
    if (autoOpenAddJob && customer?.id) {
      setShowAddJobModal(true);
    }
  }, [autoOpenAddJob, customer?.id]);
  
  // Payment methods state
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);
  const [stripeInfo, setStripeInfo] = useState(null);
  const [showStripeConfirm, setShowStripeConfirm] = useState(false);
  
  // Notification settings state
  // Check if customer has auto-payment (Stripe card or GoCardless mandate)
  const hasAutoPayment = !!(customer?.stripe_customer_id || customer?.gocardless_mandate_id);
  
  // Email invoices - default OFF if auto-payment is active
  const [emailInvoicesEnabled, setEmailInvoicesEnabled] = useState(
    customer?.email_invoices_enabled !== undefined 
      ? customer.email_invoices_enabled 
      : !hasAutoPayment
  );
  
  // SMS invoices - default OFF if auto-payment is active  
  const [smsInvoicesEnabled, setSmsInvoicesEnabled] = useState(
    customer?.sms_invoices_enabled !== undefined 
      ? customer.sms_invoices_enabled 
      : !hasAutoPayment
  );
  
  // SMS notifications (general - reminders, updates) - always default ON
  const [smsNotificationsEnabled, setSmsNotificationsEnabled] = useState(
    customer?.sms_notifications_enabled !== false
  );
  
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [generatingPaymentLink, setGeneratingPaymentLink] = useState(false);
  const [paymentSetupLink, setPaymentSetupLink] = useState(null);
  
  // Generate payment setup link
  const handleGeneratePaymentLink = async () => {
    setGeneratingPaymentLink(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/customers/${customer.id}/payment-setup-link`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setPaymentSetupLink(data.setup_url);
        // Copy to clipboard
        await navigator.clipboard.writeText(data.setup_url);
        setShowToast('Payment link copied to clipboard!');
        setTimeout(() => setShowToast(''), 3000);
      } else {
        const err = await response.json();
        alert(err.detail || 'Failed to generate link');
      }
    } catch (error) {
      console.error('Error generating payment link:', error);
      alert('Error generating payment link');
    } finally {
      setGeneratingPaymentLink(false);
    }
  };
  
  // Update notification settings
  const updateNotificationSettings = async (field, value) => {
    setSavingNotifications(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/customers/${customer.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [field]: value })
      });
      if (response.ok) {
        // Update local state
        if (field === 'email_invoices_enabled') setEmailInvoicesEnabled(value);
        if (field === 'sms_invoices_enabled') setSmsInvoicesEnabled(value);
        if (field === 'sms_notifications_enabled') setSmsNotificationsEnabled(value);
        
        // Update parent state so it persists when closing/reopening
        if (onEditCustomer) {
          onEditCustomer({ ...customer, [field]: value });
        }
        
        setShowToast('Settings saved');
        setTimeout(() => setShowToast(''), 2000);
      } else {
        const err = await response.json();
        alert(err.detail || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving notification settings:', error);
      alert('Error saving settings');
    } finally {
      setSavingNotifications(false);
    }
  };
  
  // Save customer location update
  const handleSaveLocation = async ({ address, latitude, longitude }) => {
    try {
      const token = localStorage.getItem('streakfree_token');
      // Use the dedicated coordinates endpoint — it locks the customer with
      // `location_manually_set: true` so future bulk-geocoding won't overwrite
      // this manual correction, and it also patches future-scheduled jobs
      // for the same customer so map views reflect the new pin immediately.
      const response = await fetch(`${API_URL}/api/customers/${customer.id}/coordinates`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ latitude, longitude })
      });
      if (response.ok) {
        setCustomerLocation({ lat: latitude, lng: longitude });
        setShowToast('Pin location saved');
        setTimeout(() => setShowToast(''), 2000);
        if (onEditCustomer) {
          onEditCustomer({
            ...customer,
            latitude,
            longitude,
            location_manually_set: true,
            geolocation: { lat: latitude, lng: longitude, town_level: false, source: 'manual_pin' },
          });
        }
      } else {
        const err = await response.json().catch(() => ({}));
        alert(err.detail || 'Failed to save location');
      }
    } catch (error) {
      console.error('Error saving location:', error);
      alert('Error saving location');
    }
    setShowLocationPicker(false);
  };
  
  // Fetch payment methods from Stripe
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      if (!customer?.id || !customer?.stripe_customer_id) return;
      setLoadingPaymentMethods(true);
      try {
        const response = await fetch(`${API_URL}/api/customers/${customer.id}/payment-methods`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('streakfree_token')}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setPaymentMethods(data.payment_methods || []);
          setStripeInfo(data);
        }
      } catch (error) {
        console.error('Error fetching payment methods:', error);
      } finally {
        setLoadingPaymentMethods(false);
      }
    };
    fetchPaymentMethods();
  }, [customer?.id, customer?.stripe_customer_id]);
  
  // Fetch customer's jobs from API
  useEffect(() => {
    const fetchCustomerJobs = async () => {
      if (!customer?.id) return;
      setLoadingJobs(true);
      try {
        const response = await fetch(`${API_URL}/api/jobs?customer_id=${customer.id}&limit=50`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('streakfree_token')}`
          }
        });
        if (response.ok) {
          const jobs = await response.json();
          setAllCustomerJobs(jobs);
          
          // Get today's date for comparison
          const today = toLocalYMD(new Date());
          
          // Group jobs by service type and keep only ONE job per service
          // Priority: scheduled future job > scheduled past job > completed job
          const serviceMap = new Map();
          
          for (const job of jobs) {
            // Create a normalized service key
            const serviceKey = (job.services || job.service || job.service_type || 'Window Clean').toLowerCase().trim();
            const existing = serviceMap.get(serviceKey);
            
            const jobIsScheduled = job.status === 'scheduled';
            const jobIsFuture = job.scheduled_date >= today;
            
            if (!existing) {
              // First job for this service - always add
              serviceMap.set(serviceKey, job);
            } else {
              // We already have a job for this service - decide which to keep
              const existingIsScheduled = existing.status === 'scheduled';
              const existingIsFuture = existing.scheduled_date >= today;
              
              // Keep the job that is:
              // 1. Scheduled + Future (highest priority)
              // 2. Scheduled + Past (if no future scheduled)
              // 3. Completed (lowest priority)
              
              const jobScore = (jobIsScheduled ? 2 : 0) + (jobIsFuture ? 1 : 0);
              const existingScore = (existingIsScheduled ? 2 : 0) + (existingIsFuture ? 1 : 0);
              
              if (jobScore > existingScore) {
                // New job is better
                serviceMap.set(serviceKey, job);
              } else if (jobScore === existingScore && jobIsScheduled && jobIsFuture && existingIsFuture) {
                // Both are scheduled future jobs - keep the one with nearest date
                if (job.scheduled_date < existing.scheduled_date) {
                  serviceMap.set(serviceKey, job);
                }
              }
            }
          }
          
          // Convert to array - should be just 1 job per service type
          const uniqueJobs = Array.from(serviceMap.values());
          uniqueJobs.sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date));
          
          console.log(`Customer ${customer.id}: ${jobs.length} total jobs -> ${uniqueJobs.length} unique jobs`);
          setCustomerJobs(uniqueJobs);
        }
      } catch (error) {
        console.error('Error fetching customer jobs:', error);
      } finally {
        setLoadingJobs(false);
      }
    };
    fetchCustomerJobs();
  }, [customer?.id, jobsRefreshTick]);
  
  // Fetch combined messages (SMS + Email) for Messages tab
  const fetchCombinedMessages = useCallback(async () => {
    if (!customer?.id) return;
    
    setLoadingCombinedMessages(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/customers/${customer.id}/messages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCombinedMessages(data.history || []);
      }
    } catch (error) {
      console.error('Error fetching combined messages:', error);
    }
    setLoadingCombinedMessages(false);
  }, [customer?.id]);

  // Fetch finance history when Finance tab is active
  const fetchFinanceHistory = useCallback(async () => {
    if (!customer?.id) return;
    
    setLoadingFinance(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/customers/${customer.id}/finance-history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFinanceHistory(data.history || []);
      }
    } catch (error) {
      console.error('Error fetching finance history:', error);
    }
    setLoadingFinance(false);
  }, [customer?.id]);

  // Fetch job times for Job Times tab
  const fetchJobTimes = useCallback(async () => {
    if (!customer?.id) return;
    
    setLoadingJobTimes(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/reports/time/customer/${customer.id}?limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setJobTimes(data.job_times || []);
      }
    } catch (error) {
      console.error('Error fetching job times:', error);
    }
    setLoadingJobTimes(false);
  }, [customer?.id]);

  useEffect(() => {
    if (activeTab === 'messages') {
      fetchCombinedMessages();
    } else if (activeTab === 'finance') {
      fetchFinanceHistory();
    } else if (activeTab === 'times') {
      fetchJobTimes();
    }
  }, [activeTab, fetchCombinedMessages, fetchFinanceHistory, fetchJobTimes]);
  
  if (!customer) return null;
  
  // Use real customer data - has_stripe is true only if they have a saved payment method
  const hasStripe = customer.has_stripe || false;
  const hasGoCardless = customer.has_gocardless || customer.goCardlessActive || false;
  const balance = localBalance || 0;
  const owes = balance > 0;  // Positive balance means they owe money
  
  const showMessage = (msg) => {
    setShowToast(msg);
    setTimeout(() => setShowToast(''), 2000);
  };
  
  const handleOpenMaps = () => {
    openGoogleMapsSearch(customer.address);
  };

  // Navigate to Work Planner with specific date
  const handleNavigateToDate = (dateStr) => {
    // Close the modal and navigate to work planner with date parameter
    if (onNavigateToDate) {
      onNavigateToDate(dateStr);
    }
  };

  // Handle editing a job
  const handleEditJob = (job) => {
    setEditingJob(job);
  };

  // Repeat job: open Add Job prefilled from an existing job so a brand-new
  // job (and invoice) gets created instead of editing/overriding the old one.
  const handleRepeatJob = (job) => {
    setNewJobService(job.service || '');
    setNewJobPrice(job.price != null ? String(job.price) : '');
    setNewJobFrequency(job.is_one_off || !job.frequency ? '0' : String(job.frequency));
    setNewJobDate('');
    setNewJobAddress(job.address || customer?.address || '');
    setNewJobInvoiceNotes(job.invoice_notes || '');
    const lines = (job.line_items || []).map(li => ({ ...li }));
    if (lines.length) {
      setNewJobPricingMode('lines');
      setNewJobLineItems(lines);
    } else {
      setNewJobPricingMode('flat');
      setNewJobLineItems([]);
    }
    setShowAddJobModal(true);
  };

  // Handle deleting a job
  const handleDeleteJob = async (job) => {
    if (!window.confirm(`Are you sure you want to delete this job?`)) return;
    
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/jobs/${job.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        // Remove job from local state
        setCustomerJobs(prev => prev.filter(j => j.id !== job.id));
        showMessage('Job deleted successfully');
      } else {
        showMessage('Failed to delete job');
      }
    } catch (err) {
      console.error('Error deleting job:', err);
      showMessage('Error deleting job');
    }
  };

  // Handle job save (after edit)
  const handleJobSave = (updatedJob) => {
    // Update job in local state
    setCustomerJobs(prev => prev.map(j => j.id === updatedJob.id ? updatedJob : j));
    showMessage('Job updated successfully');
  };

  // Handle creating a new job
  const handleCreateJob = async () => {
    // Compute the total from whichever pricing mode is active.
    const cleanLineItems = newJobLineItems
      .map(li => ({
        service: (li.service || '').trim(),
        rate: parseFloat(li.rate) || 0,
        quantity: parseFloat(li.quantity) || 1,
      }))
      .filter(li => li.service);
    const usingLines = newJobPricingMode === 'lines' && cleanLineItems.length > 0;
    const linesTotal = cleanLineItems.reduce((s, li) => s + li.rate * li.quantity, 0);
    const totalPrice = usingLines ? linesTotal : (parseFloat(newJobPrice) || 0);
    
    if (totalPrice <= 0) {
      setJobError(usingLines ? 'Add at least one service with a rate' : 'Please enter a price');
      return;
    }
    
    setSavingJob(true);
    setJobError('');
    
    try {
      const token = localStorage.getItem('streakfree_token');
      const scheduledDate = newJobDate || toLocalYMD(new Date());
      const isOneOff = newJobFrequency === '0';
      
      const jobData = {
        customer_id: customer.id,
        customer_name: customer.name,
        customer_address: newJobAddress || customer.address,
        service: usingLines ? (newJobService || cleanLineItems.map(li => li.service).join(', ')) : newJobService,
        services: usingLines ? (newJobService || cleanLineItems.map(li => li.service).join(', ')) : newJobService,
        service_prices: usingLines
          ? cleanLineItems.map(li => (li.rate * li.quantity).toFixed(2)).join(',')
          : undefined,
        price: totalPrice,
        frequency: isOneOff ? null : newJobFrequency,
        scheduled_date: scheduledDate,
        status: 'scheduled',
        address: newJobAddress || customer.address,
        zone: customer.zone,
        is_one_off: isOneOff,
        invoice_notes: newJobInvoiceNotes,
        pricing_mode: usingLines ? 'lines' : 'flat',
        line_items: usingLines ? cleanLineItems : [],
      };
      
      console.log('Creating job with data:', jobData);
      
      const response = await fetch(`${API_URL}/api/jobs`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(jobData)
      });
      
      if (response.ok) {
        const createdJob = await response.json();
        setCustomerJobs(prev => [...prev, createdJob]);
        setShowAddJobModal(false);
        // Reset form (no service prefill — user picks each time)
        setNewJobService('');
        setNewJobPrice('');
        setNewJobFrequency('6');
        setNewJobDate('');
        setNewJobAddress('');
        setNewJobInvoiceNotes('');
        setNewJobPricingMode('flat');
        setNewJobLineItems([]);
        showMessage('Job created successfully');
      } else {
        const error = await response.json();
        setJobError(error.detail || 'Failed to create job');
      }
    } catch (err) {
      console.error('Error creating job:', err);
      setJobError('Error creating job. Please try again.');
    } finally {
      setSavingJob(false);
    }
  };

  const tabs = [
    { id: 'customer', name: 'CUSTOMER', icon: User },
    { id: 'finance', name: 'FINANCE', icon: Building2 },
    { id: 'messages', name: 'MESSAGES', icon: MessageSquare },
    { id: 'times', name: 'JOB TIMES', icon: Clock },
    { id: 'alerts', name: 'ALERTS', icon: Bell },
  ];

  return createPortal(
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex justify-end" onClick={onClose}>
        <div 
          className="w-full max-w-md bg-zinc-900 h-full overflow-y-auto" 
          onClick={e => e.stopPropagation()}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{
            transform: `translateX(${swipeOffset}px)`,
            transition: swipeOffset === 0 ? 'transform 0.3s ease-out' : 'none',
            opacity: swipeOffset > 0 ? Math.max(0.3, 1 - swipeOffset / 300) : 1,
            paddingTop: 'env(safe-area-inset-top, 0px)'
          }}
        >
          {showToast && (
            <div className="fixed left-1/2 transform -translate-x-1/2 bg-zinc-900 text-white px-4 py-2 rounded-xl shadow-lg z-[110]" style={{ top: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}>
              {showToast}
            </div>
          )}
          
          {/* Header */}
          <div className="bg-zinc-900 text-white">
            <div className="flex items-center justify-between p-4">
              <button 
                onClick={onClose} 
                className="p-2 hover:bg-white/10 rounded-xl transition-colors flex items-center gap-1"
                data-testid="back-btn"
              >
                <ChevronLeft size={22} />
                <span className="text-sm">Back</span>
              </button>
              <h1 className="text-lg font-semibold flex-1 ml-3 truncate">{customer.name}</h1>
              <div className="relative">
                <button 
                  onClick={() => setShowMenu(!showMenu)}
                  className="p-2 hover:bg-white/10 rounded-xl transition-colors"
                  data-testid="customer-menu-btn"
                >
                  <MoreVertical size={20} />
                </button>
                
                {/* Dropdown Menu */}
                {showMenu && (
                  <>
                    <div className="fixed inset-0 z-[105]" onClick={() => setShowMenu(false)} />
                    <div className="absolute right-0 top-12 w-56 bg-white rounded-xl shadow-2xl z-[110] overflow-hidden border border-zinc-200">
                      <button
                        onClick={() => {
                          setShowMenu(false);
                          setShowEditForm(true);
                        }}
                        className="w-full px-4 py-3 flex items-center gap-3 text-left text-zinc-700 hover:bg-zinc-50 transition-colors"
                        data-testid="edit-customer-btn"
                      >
                        <User size={18} className="text-zinc-400" />
                        <span>Edit Customer</span>
                      </button>
                      
                      <button
                        onClick={() => {
                          setShowMenu(false);
                          // Navigate to payment setup page
                          safeOpenUrl(`/payment-setup/${customer.id}`);
                        }}
                        className="w-full px-4 py-3 flex items-center gap-3 text-left text-zinc-700 hover:bg-zinc-50 transition-colors"
                        data-testid="view-payment-page-btn"
                      >
                        <CreditCard size={18} className="text-zinc-400" />
                        <span>View Payment Page</span>
                      </button>
                      
                      <button
                        onClick={() => {
                          setShowMenu(false);
                          setShowToast('Invoice statuses refreshed');
                          setTimeout(() => setShowToast(''), 2000);
                        }}
                        className="w-full px-4 py-3 flex items-center gap-3 text-left text-zinc-700 hover:bg-zinc-50 transition-colors"
                        data-testid="refresh-invoices-btn"
                      >
                        <RefreshCw size={18} className="text-zinc-400" />
                        <span>Refresh Invoice Statuses</span>
                      </button>
                      
                      <div className="h-px bg-zinc-200 my-1" />
                      
                      <button
                        onClick={() => {
                          setShowMenu(false);
                          setShowDeleteConfirm(true);
                        }}
                        className="w-full px-4 py-3 flex items-center gap-3 text-left text-red-600 hover:bg-red-50 transition-colors"
                        data-testid="delete-customer-btn"
                      >
                        <Trash2 size={18} />
                        <span>Delete Customer</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
            
            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
              <div className="fixed inset-0 bg-black/50 z-[110] flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl">
                  <h3 className="text-lg font-semibold text-zinc-900 mb-2">Delete Customer?</h3>
                  <p className="text-zinc-600 mb-6">
                    Are you sure you want to delete <strong>{customer.name}</strong>? This action cannot be undone.
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      className="flex-1 px-4 py-2.5 rounded-xl border border-zinc-300 text-zinc-700 font-medium hover:bg-zinc-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        setShowDeleteConfirm(false);
                        if (onDeleteCustomer) onDeleteCustomer(customer.id);
                      }}
                      className="flex-1 px-4 py-2.5 rounded-xl bg-red-600 text-white font-medium hover:bg-red-700 transition-colors"
                      data-testid="confirm-delete-btn"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Edit Customer Form - Uses NewCustomerForm */}
            {showEditForm && (
              <NewCustomerForm 
                customer={customer}
                onClose={() => {
                  setShowEditForm(false);
                }}
                onSave={(updatedCustomer) => {
                  setShowEditForm(false);
                  // Update parent state with edited customer
                  if (onEditCustomer && updatedCustomer) {
                    onEditCustomer(updatedCustomer);
                  }
                }}
                onDelete={async (customerId) => {
                  if (onDeleteCustomer) {
                    await onDeleteCustomer(customerId);
                    onClose(); // Close the detail modal after deletion
                  }
                }}
                onStatusChange={async (customerId, newStatus) => {
                  if (onStatusChange) {
                    await onStatusChange(customerId, newStatus);
                  }
                }}
              />
            )}
            
            {/* Customer info bar */}
            <div className="flex items-center justify-between px-4 pb-4">
              <div className="flex items-center gap-3">
                <div 
                  className="w-14 h-14 rounded-2xl flex items-center justify-center text-white text-lg font-bold shadow-lg"
                  style={{ backgroundColor: getAvatarColor(customer.name) }}
                >
                  {getInitials(customer.name)}
                </div>
                <div>
                  <p className="text-sm text-white/60">{customer.address?.split(',')[0] || customer.zone || ''}</p>
                  {customer.phone && (
                    <a href={`tel:${customer.phone}`} className="text-emerald-400 font-medium">{customer.phone}</a>
                  )}
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold">£{localBalance.toFixed(2)}</p>
                <p className="text-xs text-white/50 mt-1">Balance</p>
              </div>
            </div>
            
            {/* Tabs */}
            <div className="flex bg-white/5">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 py-3 flex flex-col items-center gap-1 transition-all ${
                    activeTab === tab.id 
                      ? 'text-white bg-emerald-500/20 border-b-2 border-emerald-500' 
                      : 'text-white/50 hover:text-white/70 hover:bg-white/5'
                  }`}
                >
                  <tab.icon size={18} />
                  <span className="text-xs font-medium">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Content - with light background */}
          <div className="flex-1 bg-zinc-100 overflow-y-auto">
          {activeTab === 'customer' && (
            <>
              {/* Map */}
              <MiniMapPreview
                latitude={customerLocation.lat || customer.latitude}
                longitude={customerLocation.lng || customer.longitude}
                address={customer.address}
                height="192px"
                showNavigate={true}
                onClick={() => setShowLocationPicker(true)}
              />
              
              {/* Change Map Pin button */}
              <button 
                onClick={() => setShowLocationPicker(true)}
                className="w-full p-3 flex items-center gap-3 hover:bg-amber-50 text-left border-b border-gray-100 bg-amber-50/50"
              >
                <MapPin size={18} className="text-amber-500 flex-shrink-0" />
                <span className="flex-1 text-amber-700 text-sm font-medium">
                  {customer?.geolocation?.town_level ? 'Pin is approximate - tap to set exact location' : 'Change Map Pin Location'}
                </span>
                <ChevronRight size={16} className="text-amber-400" />
              </button>
              
              {/* Address */}
              <button onClick={handleOpenMaps} className="w-full p-4 flex items-start gap-3 hover:bg-gray-50 text-left border-b border-gray-100">
                <MapPin size={20} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                <span className="flex-1 text-emerald-600 underline">{customer.address}</span>
              </button>
              
              {/* Balance */}
              {owes && (
                <div className="p-4 flex items-center gap-3 bg-red-50 border-b border-gray-100">
                  <CreditCard size={20} className="text-red-500 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium text-red-700">Outstanding Balance</p>
                    <p className="text-sm text-red-600">£{Math.abs(balance).toFixed(2)} owed</p>
                  </div>
                </div>
              )}
              
              {/* Automatic Payment Method - Bespoke StreakFree Design */}
              <div className="border-b border-gray-100">
                <div className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg">
                      <CreditCard size={20} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-zinc-900">Payment Method</p>
                      <p className="text-sm text-zinc-500">
                        {loadingPaymentMethods ? 'Loading...' :
                         paymentMethods.length > 0 ? (
                           <>
                             {paymentMethods[0].brand} •••• {paymentMethods[0].last4}
                             {paymentMethods[0].is_last_used && <span className="text-emerald-600 ml-1">(last used)</span>}
                             {paymentMethods.length > 1 && <span className="text-zinc-400"> +{paymentMethods.length - 1} more</span>}
                           </>
                         ) :
                         customer.stripe_customer_id ? 'Stripe (no card on file)' :
                         customer.payment_method === 'stripe' ? 'Stripe' :
                         customer.payment_method === 'gocardless' ? 'GoCardless' :
                         'Not configured'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Saved Cards Display */}
                  {paymentMethods.length > 0 && (
                    <div className="space-y-2">
                      {paymentMethods.map((pm, idx) => (
                        <div 
                          key={pm.id || idx}
                          className={`bg-gradient-to-r rounded-xl p-4 text-white relative overflow-hidden ${
                            pm.is_last_used 
                              ? 'from-emerald-700 to-emerald-800 ring-2 ring-emerald-400' 
                              : 'from-zinc-800 to-zinc-900'
                          }`}
                        >
                          {/* Card chip design */}
                          <div className="absolute top-3 right-3 w-8 h-6 rounded bg-gradient-to-br from-amber-400 to-amber-500 opacity-80" />
                          
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                              {pm.brand === 'Visa' && (
                                <span className="text-lg font-bold italic text-blue-400">VISA</span>
                              )}
                              {pm.brand === 'Mastercard' && (
                                <span className="text-lg font-bold text-orange-400">MC</span>
                              )}
                              {pm.brand === 'Amex' && (
                                <span className="text-lg font-bold text-blue-300">AMEX</span>
                              )}
                              {!['Visa', 'Mastercard', 'Amex'].includes(pm.brand) && (
                                <span className="text-lg font-bold text-white/80">{pm.brand}</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {pm.is_last_used && (
                                <span className="text-[10px] px-2 py-0.5 bg-emerald-500 rounded-full font-medium">LAST USED</span>
                              )}
                              {pm.is_default && !pm.is_last_used && (
                                <span className="text-[10px] px-2 py-0.5 bg-blue-500 rounded-full font-medium">DEFAULT</span>
                              )}
                            </div>
                          </div>
                          
                          <div className="font-mono text-lg tracking-wider mb-3">
                            •••• •••• •••• {pm.last4}
                          </div>
                          
                          <div className="flex items-center justify-between text-xs text-white/60">
                            <span>Expires {pm.exp_month.toString().padStart(2, '0')}/{pm.exp_year.toString().slice(-2)}</span>
                            <span className="text-emerald-400 font-medium">Active</span>
                          </div>
                        </div>
                      ))}
                      
                      {/* Disable Stripe auto-pay button — useful when the saved card is dead
                          and the customer should fall back to GoCardless / manual */}
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Stop using ${customer.name}'s saved card for auto-pay?\n\nFuture job completions will no longer attempt to charge the card. The card stays in Stripe (you can re-enable later) — but GoCardless / manual payment will take over for now.`)) return;
                          try {
                            const token = localStorage.getItem('streakfree_token');
                            const res = await fetch(`${API_URL}/api/customers/${customer.id}`, {
                              method: 'PATCH',
                              headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                              body: JSON.stringify({ stripe_has_payment_method: false }),
                            });
                            if (res.ok) {
                              setPaymentMethods([]);
                              setStripeInfo(null);
                              // Mutate the customer object in place so the rest of the
                              // modal (badges, charge-confirm picker) reacts immediately.
                              customer.stripe_has_payment_method = false;
                              alert('Stripe auto-pay disabled — future jobs will use GoCardless / manual payment.');
                            } else {
                              const err = await res.json().catch(() => ({}));
                              alert(err.detail || 'Failed to disable Stripe auto-pay');
                            }
                          } catch (e) {
                            alert('Network error');
                          }
                        }}
                        className="w-full mt-2 px-3 py-2 bg-zinc-50 hover:bg-red-50 border border-zinc-200 hover:border-red-200 text-zinc-700 hover:text-red-700 rounded-lg text-xs font-medium transition-colors"
                        data-testid="disable-stripe-autopay-btn"
                      >
                        Stop using card for auto-pay
                      </button>
                    </div>
                  )}
                  
                  {/* GoCardless Direct Debit Display */}
                  {customer?.gocardless_mandate_id && (
                    <div className="mt-2">
                      <div className="bg-gradient-to-r from-cyan-700 to-cyan-800 rounded-xl p-4 text-white relative overflow-hidden">
                        {/* Bank icon design */}
                        <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-cyan-500/30 flex items-center justify-center">
                          <Building2 size={16} className="text-white" />
                        </div>
                        
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-lg font-bold text-white">GoCardless</span>
                          <span className="text-[10px] px-2 py-0.5 bg-cyan-500 rounded-full font-medium">DIRECT DEBIT</span>
                        </div>
                        
                        <div className="font-mono text-sm tracking-wider mb-2 text-cyan-100">
                          {customer.gocardless_mandate_id}
                        </div>
                        
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-cyan-200">Status: {customer.gocardless_mandate_status || 'Active'}</span>
                          <span className="text-cyan-300 font-medium">Bank Transfer</span>
                        </div>
                      </div>
                      
                      {/* View in GoCardless link */}
                      <a
                        href={`https://manage.gocardless.com/mandates/${customer.gocardless_mandate_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full mt-2 flex items-center justify-between p-3 bg-cyan-50 hover:bg-cyan-100 rounded-xl transition-colors group"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-cyan-600 flex items-center justify-center">
                            <span className="text-white font-bold text-xs">GC</span>
                          </div>
                          <div className="text-left">
                            <p className="text-sm font-medium text-cyan-900">View in GoCardless</p>
                            <p className="text-xs text-cyan-600">Manage Direct Debit</p>
                          </div>
                        </div>
                        <ExternalLink size={18} className="text-cyan-400 group-hover:text-cyan-600 transition-colors" />
                      </a>
                    </div>
                  )}
                  
                  {/* Stripe Dashboard Link */}
                  {stripeInfo?.stripe_dashboard_url && (
                    <button
                      onClick={() => setShowStripeConfirm(true)}
                      className="w-full mt-3 flex items-center justify-between p-3 bg-zinc-50 hover:bg-zinc-100 rounded-xl transition-colors group"
                      data-testid="view-stripe-dashboard-btn"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-[#635bff] flex items-center justify-center">
                          <span className="text-white font-bold text-xs">S</span>
                        </div>
                        <div className="text-left">
                          <p className="text-sm font-medium text-zinc-900">View in Stripe</p>
                          <p className="text-xs text-zinc-500">Manage payment methods</p>
                        </div>
                      </div>
                      <ChevronRight size={18} className="text-zinc-400 group-hover:text-zinc-600 transition-colors" />
                    </button>
                  )}
                  
                  {/* Payment Setup Link Section */}
                  <div className="mt-4 pt-4 border-t border-zinc-100">
                    <p className="text-xs font-medium text-zinc-500 uppercase tracking-wide mb-3">Invite Customer to Add Card</p>
                    
                    {paymentSetupLink ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-xl">
                          <Check size={16} className="text-emerald-600" />
                          <span className="text-sm text-emerald-700 flex-1">Link copied!</span>
                        </div>
                        <input
                          type="text"
                          value={paymentSetupLink}
                          readOnly
                          className="w-full p-2 text-xs text-zinc-500 bg-zinc-50 rounded-lg border border-zinc-200"
                          onClick={(e) => {
                            e.target.select();
                            navigator.clipboard.writeText(paymentSetupLink);
                            setShowToast('Copied!');
                            setTimeout(() => setShowToast(''), 2000);
                          }}
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(paymentSetupLink);
                              setShowToast('Link copied!');
                              setTimeout(() => setShowToast(''), 2000);
                            }}
                            className="flex-1 flex items-center justify-center gap-2 p-2.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 rounded-xl text-sm font-medium transition-colors"
                          >
                            <Copy size={16} />
                            Copy Link
                          </button>
                          <button
                            onClick={() => setPaymentSetupLink(null)}
                            className="px-4 py-2.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 rounded-xl text-sm transition-colors"
                          >
                            New Link
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <button
                          onClick={handleGeneratePaymentLink}
                          disabled={generatingPaymentLink}
                          className="w-full flex items-center justify-center gap-2 p-3 bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white rounded-xl transition-colors font-medium"
                        >
                          {generatingPaymentLink ? (
                            <>
                              <Loader2 size={18} className="animate-spin" />
                              <span>Generating...</span>
                            </>
                          ) : (
                            <>
                              <Link2 size={18} />
                              <span>Generate Payment Link</span>
                            </>
                          )}
                        </button>
                        <p className="text-xs text-zinc-500 text-center">
                          Send this link to {customer.name?.split(' ')[0] || 'customer'} to add their card details
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Stripe Confirmation Modal */}
              {showStripeConfirm && (
                <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4" onClick={() => setShowStripeConfirm(false)}>
                  <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl" onClick={e => e.stopPropagation()}>
                    <div className="w-14 h-14 rounded-2xl bg-[#635bff] flex items-center justify-center mx-auto mb-4">
                      <span className="text-white font-bold text-xl">S</span>
                    </div>
                    <h3 className="text-lg font-semibold text-zinc-900 text-center mb-2">Open Stripe Dashboard?</h3>
                    <p className="text-zinc-600 text-center mb-6 text-sm">
                      You'll be taken to Stripe to view and manage {customer.name}'s payment details.
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={() => setShowStripeConfirm(false)}
                        className="flex-1 px-4 py-2.5 rounded-xl border border-zinc-300 text-zinc-700 font-medium hover:bg-zinc-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => {
                          setShowStripeConfirm(false);
                          safeOpenUrl(stripeInfo.stripe_dashboard_url);
                        }}
                        className="flex-1 px-4 py-2.5 rounded-xl bg-[#635bff] text-white font-medium hover:bg-[#5851ea] transition-colors"
                      >
                        Open Stripe
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Notes and Activity */}
              <ClickableRow icon={ClipboardList} onClick={() => setShowNotesPanel(!showNotesPanel)}>
                <div>
                  <p className="font-medium">Notes and Activity</p>
                  <p className="text-sm text-gray-500">{localNotes ? 'Tap to edit notes' : 'Tap to add notes'}</p>
                </div>
                <ChevronRight size={16} className={`text-gray-400 transition-transform ${showNotesPanel ? 'rotate-90' : ''}`} />
              </ClickableRow>
              
              {/* Expanded Notes Panel */}
              {showNotesPanel && (
                <div className="p-4 bg-gray-50 border-b border-gray-100 space-y-3">
                  <CustomerNotesEditor
                    customerId={customer.id}
                    initialNotes={localNotes}
                    onSaved={(v) => {
                      setLocalNotes(v);
                      // Optimistically mirror onto the customer object so any
                      // sibling component reading customer.notes sees the update
                      if (customer) customer.notes = v;
                      try { customerChanged(Actions.UPDATE, { id: customer.id, notes: v }); } catch (_) { /* ignore */ }
                    }}
                  />
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase mb-1">Source</p>
                    <p className="text-sm text-gray-600">{customer.source === 'email' ? 'Added via Email' : customer.source === 'csv_import' ? 'CSV Import' : customer.source || 'Manual'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase mb-1">Created</p>
                    <p className="text-sm text-gray-600">{customer.created_at ? new Date(customer.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Unknown'}</p>
                  </div>
                </div>
              )}
              
              {/* Customer Photos */}
              <div className="p-4 border-b border-gray-100 space-y-3">
                <CustomerPhotos customerId={customer.id} title="Photos" />
                <CustomerNotesEditor
                  customerId={customer.id}
                  initialNotes={localNotes}
                  title="Notes"
                  onSaved={(v) => {
                    setLocalNotes(v);
                    if (customer) customer.notes = v;
                    try { customerChanged(Actions.UPDATE, { id: customer.id, notes: v }); } catch (_) { /* ignore */ }
                  }}
                />
              </div>
              
              {/* Stripe Info */}
              {hasStripe && (
                <button 
                  className="w-full p-4 flex items-center gap-3 hover:bg-gray-50 text-left border-b border-gray-100"
                  onClick={() => showMessage('Opening Stripe info...')}
                >
                  <span className="bg-[#635BFF] text-white w-6 h-6 text-xs rounded font-bold flex items-center justify-center">S</span>
                  <div>
                    <p className="font-medium">View Stripe Information</p>
                    <p className="text-sm text-gray-500">Stripe payment active</p>
                  </div>
                </button>
              )}
              
              {/* GoCardless Info */}
              {hasGoCardless && (
                <button 
                  className="w-full p-4 flex items-center gap-3 hover:bg-gray-50 text-left border-b border-gray-100"
                  onClick={() => showMessage('Opening GoCardless info...')}
                >
                  <span className="bg-[#00B4AA] text-white w-6 h-6 text-xs rounded font-bold flex items-center justify-center">GC</span>
                  <div>
                    <p className="font-medium">View GoCardless Information</p>
                    <p className="text-sm text-gray-500">Direct Debit active</p>
                  </div>
                </button>
              )}
              
              {/* Notification Settings */}
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
                    <Bell size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-zinc-900">Invoice & Notifications</p>
                    <p className="text-sm text-zinc-500">
                      {(hasStripe || hasGoCardless) ? 
                        'Auto-payment enabled - invoices optional' : 
                        'Configure how this customer receives invoices'}
                    </p>
                  </div>
                </div>
                
                <div className="space-y-3 ml-13">
                  {/* SMS Notifications Toggle (General - reminders, updates) */}
                  <div className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-zinc-900">SMS Notifications</p>
                      <p className="text-xs text-zinc-500">Reminders, job updates & confirmations</p>
                    </div>
                    <button
                      onClick={() => updateNotificationSettings('sms_notifications_enabled', !smsNotificationsEnabled)}
                      disabled={savingNotifications}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        smsNotificationsEnabled ? 'bg-emerald-500' : 'bg-zinc-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          smsNotificationsEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  
                  {/* Divider with label */}
                  <div className="flex items-center gap-2 py-1">
                    <div className="flex-1 h-px bg-zinc-200"></div>
                    <span className="text-xs text-zinc-400 font-medium">INVOICING</span>
                    <div className="flex-1 h-px bg-zinc-200"></div>
                  </div>
                  
                  {/* Email Invoice Toggle */}
                  <div className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-zinc-900">Email Invoices</p>
                      <p className="text-xs text-zinc-500">
                        {hasAutoPayment ? 
                          'Auto-payment active - not needed' : 
                          'Send invoices via email'}
                      </p>
                    </div>
                    <button
                      onClick={() => updateNotificationSettings('email_invoices_enabled', !emailInvoicesEnabled)}
                      disabled={savingNotifications}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        emailInvoicesEnabled ? 'bg-emerald-500' : 'bg-zinc-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          emailInvoicesEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  
                  {/* SMS Invoice Toggle */}
                  <div className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-zinc-900">SMS Invoices</p>
                      <p className="text-xs text-zinc-500">
                        {hasAutoPayment ? 
                          'Auto-payment active - not needed' : 
                          'Send invoice links via text'}
                      </p>
                    </div>
                    <button
                      onClick={() => updateNotificationSettings('sms_invoices_enabled', !smsInvoicesEnabled)}
                      disabled={savingNotifications}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        smsInvoicesEnabled ? 'bg-emerald-500' : 'bg-zinc-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          smsInvoicesEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  
                  {/* Info message for auto-payment customers */}
                  {hasAutoPayment && (
                    <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <p className="text-xs text-amber-800">
                        <strong>Auto-payment active:</strong> Payments are collected automatically.
                        If a payment fails, you'll be prompted to send an invoice.
                      </p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Xero Info */}
              <button 
                className="w-full p-4 flex items-center gap-3 hover:bg-gray-50 text-left border-b border-gray-100"
                onClick={() => showMessage('Opening Xero info...')}
              >
                <span className="bg-[#13B5EA] text-white px-1.5 py-0.5 text-xs rounded font-bold">xero</span>
                <span className="font-medium">View Xero Information</span>
              </button>
              
              {/* Jobs Section */}
              <div className="bg-gray-100 py-4">
                <div className="flex items-center justify-between px-4 mb-3">
                  <h2 className="text-sm font-medium text-gray-700">Jobs</h2>
                  <button
                    onClick={() => setShowAddJobModal(true)}
                    className="flex items-center gap-1 px-3 py-1.5 bg-emerald-500 text-white text-xs font-medium rounded-lg hover:bg-emerald-600 transition-colors"
                    data-testid="add-job-btn"
                  >
                    <Plus size={14} />
                    Add Job
                  </button>
                </div>
                
                {loadingJobs ? (
                  <div className="text-center py-8 text-gray-500">
                    Loading jobs...
                  </div>
                ) : customerJobs.length > 0 ? (
                  customerJobs.map(job => (
                    <CustomerJobCard 
                      key={job.id} 
                      job={job}
                      onClick={() => setSelectedJob(job)}
                      onNavigateToDate={handleNavigateToDate}
                      onEditJob={handleEditJob}
                      onDeleteJob={handleDeleteJob}
                      onRepeatJob={handleRepeatJob}
                      onOpenVisit={(job, dateStr, wantCompleted) => {
                        const day = (dateStr || '').slice(0, 10);
                        const doneStatuses = ['completed', 'complete', 'done'];
                        const match = allCustomerJobs.find(j =>
                          (j.scheduled_date || '').slice(0, 10) === day &&
                          (wantCompleted ? doneStatuses.includes(j.status) : !doneStatuses.includes(j.status))
                        );
                        setSelectedJob(match || job);
                      }}
                    />
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No jobs for this customer
                  </div>
                )}
              </div>
            </>
          )}
          
          {activeTab === 'finance' && (
            <div className="p-4 space-y-4">
              {/* Balance Card */}
              <div className="bg-gradient-to-br from-zinc-800 to-zinc-900 rounded-xl p-5 text-white">
                <p className="text-sm text-white/70 mb-1">Account Balance</p>
                <p className="text-3xl font-bold">£{localBalance?.toFixed(2) || '0.00'}</p>
                {localBalance > 0 && (
                  <p className="text-amber-400 text-sm mt-2">Outstanding balance</p>
                )}
              </div>

              {/* Take Payment Button */}
              <button
                onClick={() => setShowTakePayment(true)}
                className="w-full flex items-center justify-center gap-3 p-4 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-xl shadow-lg transition-all"
                data-testid="take-payment-btn"
              >
                <Banknote size={22} />
                <span className="text-lg font-semibold">Take Payment</span>
              </button>

              {/* Payment Method Info */}
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <h4 className="font-semibold text-zinc-900 mb-3">Payment Method</h4>
                {customer?.has_stripe ? (
                  <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <CreditCard size={18} className="text-emerald-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-emerald-800">Stripe Connected</p>
                      <p className="text-xs text-emerald-600">Auto-charge enabled</p>
                    </div>
                    <Check size={18} className="text-emerald-600" />
                  </div>
                ) : customer?.stripe_customer_id ? (
                  <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <CreditCard size={18} className="text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-amber-800">Stripe Linked</p>
                      <p className="text-xs text-amber-600">No payment card on file</p>
                    </div>
                  </div>
                ) : customer?.payment_method === 'gocardless' || customer?.gocardless_customer_id ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-3 p-3 bg-cyan-50 rounded-lg border border-cyan-200">
                      <div className="p-2 bg-cyan-100 rounded-lg">
                        <Building2 size={18} className="text-cyan-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-cyan-800">GoCardless Connected</p>
                        <p className="text-xs text-cyan-600">
                          {customer?.gocardless_mandate_id ? (
                            <>Direct Debit: {customer.gocardless_mandate_id}</>
                          ) : (
                            <>Direct Debit active</>
                          )}
                        </p>
                      </div>
                      <Check size={18} className="text-cyan-600" />
                    </div>
                    {customer?.gocardless_mandate_id && (
                      <a 
                        href={`https://manage.gocardless.com/mandates/${customer.gocardless_mandate_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-2 text-sm text-cyan-700 hover:bg-cyan-50 rounded-lg transition-colors"
                      >
                        <ExternalLink size={14} />
                        View GoCardless Information
                      </a>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-3 p-3 bg-zinc-50 rounded-lg">
                    <div className="p-2 bg-zinc-100 rounded-lg">
                      <CreditCard size={18} className="text-zinc-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-zinc-700">No payment method</p>
                      <p className="text-xs text-zinc-500">Manual payments</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Finance History */}
              <div className="bg-white rounded-xl shadow-sm">
                <h4 className="font-semibold text-zinc-900 p-4 border-b">Transaction History</h4>
                {loadingFinance ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : financeHistory.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-zinc-400">
                    <FileText size={32} className="mb-2" />
                    <p className="text-sm">No transactions yet</p>
                  </div>
                ) : (
                  <div className="divide-y divide-zinc-100">
                    {financeHistory.map((dayGroup, groupIndex) => (
                      <div key={groupIndex}>
                        {/* Date Header */}
                        <div className="px-4 py-2 bg-zinc-50 flex items-center justify-between">
                          <span className="text-sm font-medium text-zinc-600">
                            {dayGroup.date === toLocalYMD(new Date()) ? 'Today' : 
                             new Date(dayGroup.date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                          </span>
                          <span className="text-sm text-zinc-500">
                            £{(dayGroup.balance_change || 0).toFixed(2)}
                          </span>
                        </div>
                        {/* Entries for this day */}
                        {dayGroup.entries.map((entry, entryIndex) => (
                          <div 
                            key={entryIndex} 
                            className={`px-4 py-3 flex items-start gap-3 ${entry.type === 'invoice' ? 'cursor-pointer hover:bg-zinc-100' : 'hover:bg-zinc-50'}`}
                            onClick={() => {
                              if (entry.type === 'invoice') {
                                setSelectedInvoice(entry);
                              }
                            }}
                          >
                            {/* Icon */}
                            <div className={`p-2 rounded-lg ${entry.type === 'invoice' ? 'bg-blue-100' : 'bg-emerald-100'}`}>
                              {entry.type === 'invoice' ? (
                                <FileText size={16} className="text-blue-600" />
                              ) : (
                                <RefreshCw size={16} className="text-emerald-600" />
                              )}
                            </div>
                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-zinc-900 text-sm">
                                  {entry.type === 'invoice' ? `Invoice ${entry.invoice_number || ''}` : entry.is_group ? 'Account Payment' : `${(entry.provider || 'Stripe').charAt(0).toUpperCase() + (entry.provider || 'Stripe').slice(1)} Payment`}
                                </span>
                                {entry.is_group && (
                                  <span data-testid="paid-together-badge" className="px-2 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-700 rounded-full">
                                    {(entry.invoices_paid || []).length} paid together
                                  </span>
                                )}
                                {entry.is_group && entry.status === 'pending' && (
                                  <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">Pending</span>
                                )}
                                {entry.type === 'invoice' && entry.status === 'paid' && (
                                  <span className="px-2 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-700 rounded-full">Paid</span>
                                )}
                                {entry.type === 'invoice' && entry.status === 'pending' && (
                                  <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">Pending</span>
                                )}
                                {entry.type === 'payment' && (
                                  <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded-full">
                                    {(entry.provider || 'Stripe').toUpperCase()}
                                  </span>
                                )}
                              </div>
                              {/* Email/SMS Delivery Badges */}
                              {entry.type === 'invoice' && (entry.email_sent || entry.sms_sent) && (
                                <div className="flex items-center gap-2 mt-1">
                                  {entry.email_sent && (
                                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded ${
                                      entry.email_status === 'opened' ? 'bg-emerald-100 text-emerald-700' :
                                      entry.email_status === 'delivered' ? 'bg-blue-100 text-blue-700' :
                                      entry.email_status === 'bounced' ? 'bg-red-100 text-red-700' :
                                      'bg-zinc-100 text-zinc-600'
                                    }`}>
                                      <Mail size={10} />
                                      {entry.email_status === 'opened' ? '✓✓' : entry.email_status === 'delivered' ? '✓' : entry.email_status === 'bounced' ? '✗' : '...'}
                                    </span>
                                  )}
                                  {entry.sms_sent && (
                                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded ${
                                      entry.sms_status === 'delivered' ? 'bg-emerald-100 text-emerald-700' :
                                      entry.sms_status === 'sent' ? 'bg-blue-100 text-blue-700' :
                                      entry.sms_status === 'failed' ? 'bg-red-100 text-red-700' :
                                      'bg-zinc-100 text-zinc-600'
                                    }`}>
                                      <MessageSquare size={10} />
                                      {entry.sms_status === 'delivered' ? '✓✓' : entry.sms_status === 'sent' ? '✓' : entry.sms_status === 'failed' ? '✗' : '...'}
                                    </span>
                                  )}
                                </div>
                              )}
                              <p className="text-xs text-zinc-500 mt-0.5">
                                {new Date(entry.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                {entry.payment_method && ` • ${entry.payment_method}`}
                              </p>
                              {/* Part-payment audit trail: invoices covered by this account payment */}
                              {entry.is_group && (entry.invoices_paid || []).length > 0 && (
                                <div data-testid="paid-together-invoice-list" className="mt-2 rounded-lg border border-emerald-100 bg-emerald-50/60 divide-y divide-emerald-100">
                                  {entry.invoices_paid.map((pi, piIdx) => (
                                    <div key={piIdx} className="flex items-center justify-between px-2.5 py-1.5 text-xs">
                                      <span className="text-zinc-700 flex items-center gap-1.5">
                                        <FileText size={11} className="text-emerald-600" />
                                        {pi.invoice_number ? `Invoice ${pi.invoice_number}` : 'Invoice'}
                                      </span>
                                      <span className="font-medium text-zinc-800">£{(pi.amount || 0).toFixed(2)}</span>
                                    </div>
                                  ))}
                                  {entry.reference && (
                                    <div className="px-2.5 py-1 text-[10px] text-zinc-400 truncate" title={entry.reference}>
                                      Ref: {entry.reference.slice(-14)}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            {/* Amount + Actions */}
                            <div className="text-right flex flex-col items-end gap-1">
                              <span className={`font-semibold ${entry.type === 'payment' ? 'text-emerald-600' : 'text-zinc-900'}`}>
                                {entry.type === 'payment' ? '-' : ''}£{(entry.amount || 0).toFixed(2)}
                              </span>
                              {/* Resend Invoice buttons for pending invoices */}
                              {entry.type === 'invoice' && entry.status === 'pending' && (
                                <div className="flex items-center gap-1">
                                  {customer?.email && (
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        const key = `${entry.id}-email`;
                                        if (sendingInvoiceIds[key]) return;
                                        const amt = (entry.amount || 0).toFixed(2);
                                        const confirmMsg = `Send invoice email to ${customer?.name || 'this customer'} (${customer?.email}) for £${amt}?`;
                                        if (!window.confirm(confirmMsg)) return;
                                        setSendingInvoiceIds(prev => ({ ...prev, [key]: true }));
                                        try {
                                          const token = localStorage.getItem('streakfree_token');
                                          const res = await fetch(`${API_URL}/api/invoices/${entry.id}/resend`, {
                                            method: 'POST',
                                            headers: {
                                              'Authorization': `Bearer ${token}`,
                                              'Content-Type': 'application/json'
                                            },
                                            body: JSON.stringify({ method: 'email' })
                                          });
                                          if (res.ok) {
                                            toast.success('Email sent!');
                                            fetchFinanceHistory();
                                          } else {
                                            const err = await res.json();
                                            toast.error(err.detail || 'Failed to send email');
                                          }
                                        } catch (err) {
                                          toast.error('Failed to send email');
                                        } finally {
                                          setSendingInvoiceIds(prev => {
                                            const next = { ...prev };
                                            delete next[key];
                                            return next;
                                          });
                                        }
                                      }}
                                      disabled={!!sendingInvoiceIds[`${entry.id}-email`]}
                                      className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-zinc-300 disabled:cursor-not-allowed font-medium flex items-center gap-1"
                                      title="Resend invoice via email"
                                      data-testid={`resend-invoice-email-${entry.id}`}
                                    >
                                      {sendingInvoiceIds[`${entry.id}-email`] ? (
                                        <Loader2 size={12} className="animate-spin" />
                                      ) : (
                                        <Mail size={12} />
                                      )}
                                    </button>
                                  )}
                                  {customer?.phone && (
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        const key = `${entry.id}-whatsapp`;
                                        if (sendingInvoiceIds[key]) return;
                                        const amt = (entry.amount || 0).toFixed(2);
                                        const confirmMsg = `Send WhatsApp invoice reminder to ${customer?.name || 'this customer'} (${customer?.phone}) for £${amt}?\n\nThey will receive an "invoice_ready" template with a Pay Now button.`;
                                        if (!window.confirm(confirmMsg)) return;
                                        setSendingInvoiceIds(prev => ({ ...prev, [key]: true }));
                                        try {
                                          const token = localStorage.getItem('streakfree_token');
                                          const res = await fetch(`${API_URL}/api/invoices/${entry.id}/resend`, {
                                            method: 'POST',
                                            headers: {
                                              'Authorization': `Bearer ${token}`,
                                              'Content-Type': 'application/json'
                                            },
                                            body: JSON.stringify({ method: 'whatsapp' })
                                          });
                                          if (res.ok) {
                                            const data = await res.json().catch(() => ({}));
                                            const waResult = data?.results?.whatsapp;
                                            if (waResult && waResult.status === 'success') {
                                              toast.success('WhatsApp sent!');
                                              fetchFinanceHistory();
                                            } else {
                                              toast.error(waResult?.error || 'WhatsApp send failed');
                                            }
                                          } else {
                                            const err = await res.json().catch(() => ({}));
                                            toast.error(err.detail || 'Failed to send WhatsApp');
                                          }
                                        } catch (err) {
                                          toast.error('Failed to send WhatsApp');
                                        } finally {
                                          setSendingInvoiceIds(prev => {
                                            const next = { ...prev };
                                            delete next[key];
                                            return next;
                                          });
                                        }
                                      }}
                                      disabled={!!sendingInvoiceIds[`${entry.id}-whatsapp`]}
                                      className="text-xs px-2 py-1 bg-[#25d366] text-white rounded hover:bg-[#1ebe5b] disabled:bg-zinc-300 disabled:cursor-not-allowed font-medium flex items-center gap-1"
                                      title="Resend invoice via WhatsApp"
                                      data-testid={`resend-invoice-whatsapp-${entry.id}`}
                                    >
                                      {sendingInvoiceIds[`${entry.id}-whatsapp`] ? (
                                        <Loader2 size={12} className="animate-spin" />
                                      ) : (
                                        <svg viewBox="0 0 24 24" className="w-3 h-3" fill="currentColor" aria-hidden="true">
                                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                                        </svg>
                                      )}
                                    </button>
                                  )}
                                  {customer?.phone && (
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        const key = `${entry.id}-sms`;
                                        if (sendingInvoiceIds[key]) return;
                                        const amt = (entry.amount || 0).toFixed(2);
                                        const confirmMsg = `Send invoice SMS to ${customer?.name || 'this customer'} (${customer?.phone}) for £${amt}?`;
                                        if (!window.confirm(confirmMsg)) return;
                                        setSendingInvoiceIds(prev => ({ ...prev, [key]: true }));
                                        try {
                                          const token = localStorage.getItem('streakfree_token');
                                          const res = await fetch(`${API_URL}/api/invoices/${entry.id}/resend`, {
                                            method: 'POST',
                                            headers: {
                                              'Authorization': `Bearer ${token}`,
                                              'Content-Type': 'application/json'
                                            },
                                            body: JSON.stringify({ method: 'sms' })
                                          });
                                          if (res.ok) {
                                            toast.success('SMS sent!');
                                            fetchFinanceHistory();
                                          } else {
                                            const err = await res.json();
                                            toast.error(err.detail || 'Failed to send SMS');
                                          }
                                        } catch (err) {
                                          toast.error('Failed to send SMS');
                                        } finally {
                                          setSendingInvoiceIds(prev => {
                                            const next = { ...prev };
                                            delete next[key];
                                            return next;
                                          });
                                        }
                                      }}
                                      disabled={!!sendingInvoiceIds[`${entry.id}-sms`]}
                                      className="text-xs px-2 py-1 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:bg-zinc-300 disabled:cursor-not-allowed font-medium flex items-center gap-1"
                                      title="Resend invoice via SMS"
                                      data-testid={`resend-invoice-sms-${entry.id}`}
                                    >
                                      {sendingInvoiceIds[`${entry.id}-sms`] ? (
                                        <Loader2 size={12} className="animate-spin" />
                                      ) : (
                                        <MessageSquare size={12} />
                                      )}
                                    </button>
                                  )}
                                </div>
                              )}
                              {/* Delete invoice — works for ANY non-cancelled
                                  invoice (pending or paid). Use case: duplicate
                                  invoices accidentally created when a job got
                                  marked Done twice. We CANCEL the invoice
                                  (preserves audit trail) and rebate the
                                  customer's balance if it was pending. */}
                              {entry.type === 'invoice' && entry.status !== 'cancelled' && (
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    const key = `${entry.id}-delete`;
                                    if (sendingInvoiceIds[key]) return;
                                    const amt = (entry.amount || 0).toFixed(2);
                                    const wasPaid = entry.status === 'paid';
                                    const confirmMsg = wasPaid
                                      ? `Delete invoice ${entry.invoice_number} (£${amt})?\n\nThis was already PAID — the payment record will stay but the invoice will be marked as cancelled.`
                                      : `Delete invoice ${entry.invoice_number} (£${amt})?\n\n${customer?.name || 'This customer'}'s balance will be reduced by £${amt}.`;
                                    if (!window.confirm(confirmMsg)) return;
                                    setSendingInvoiceIds(prev => ({ ...prev, [key]: true }));
                                    try {
                                      const token = localStorage.getItem('streakfree_token');
                                      const res = await fetch(`${API_URL}/api/invoices/${entry.id}`, {
                                        method: 'DELETE',
                                        headers: { 'Authorization': `Bearer ${token}` },
                                      });
                                      if (res.ok) {
                                        toast.success(`Invoice ${entry.invoice_number} deleted`);
                                        fetchFinanceHistory();
                                        // Refresh customer balance optimistically
                                        if (!wasPaid && customer?.id) {
                                          setLocalBalance((b) => Math.max(0, (b || 0) - (entry.amount || 0)));
                                        }
                                      } else {
                                        const err = await res.json().catch(() => ({}));
                                        toast.error(err.detail || 'Failed to delete invoice');
                                      }
                                    } catch (err) {
                                      toast.error('Failed to delete invoice');
                                    } finally {
                                      setSendingInvoiceIds(prev => {
                                        const next = { ...prev };
                                        delete next[key];
                                        return next;
                                      });
                                    }
                                  }}
                                  disabled={!!sendingInvoiceIds[`${entry.id}-delete`]}
                                  className="text-xs px-2 py-1 bg-red-50 text-red-600 hover:bg-red-100 rounded font-medium flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Delete invoice"
                                  data-testid={`delete-invoice-${entry.id}`}
                                >
                                  {sendingInvoiceIds[`${entry.id}-delete`] ? (
                                    <Loader2 size={12} className="animate-spin" />
                                  ) : (
                                    <Trash2 size={12} />
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {activeTab === 'messages' && (
            <div className="flex flex-col h-full p-4">
              {/* Messages List - Combined SMS + Email */}
              <div className="flex-1 overflow-y-auto space-y-4 mb-4">
                {loadingCombinedMessages ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="animate-spin text-zinc-400" size={24} />
                  </div>
                ) : combinedMessages.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageSquare size={48} className="mx-auto mb-4 text-zinc-300" />
                    <p className="text-zinc-600 font-medium">No messages yet</p>
                    <p className="text-sm text-zinc-400 mt-1">Send an SMS or email to start the conversation</p>
                  </div>
                ) : (
                  combinedMessages.map((dayGroup, groupIndex) => (
                    <div key={groupIndex}>
                      {/* Date Header */}
                      <div className="text-center mb-3">
                        <span className="text-xs font-medium text-zinc-500 bg-zinc-100 px-3 py-1 rounded-full">
                          {dayGroup.date === toLocalYMD(new Date()) ? 'Today' : 
                           new Date(dayGroup.date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                        </span>
                      </div>
                      
                      {/* Messages for this day */}
                      <div className="space-y-3">
                        {dayGroup.messages.map((msg, msgIndex) => {
                          const isWhatsApp = msg.type === 'whatsapp';
                          const isEmail = msg.type === 'email';
                          // Render delivery status indicators
                          const renderDeliveryStatus = () => {
                            if (isWhatsApp) {
                              const ds = msg.delivery_status || msg.status;
                              if (ds === 'read') {
                                return (
                                  <div className="flex items-center gap-1" title="Read">
                                    <CheckCheck size={14} className="text-blue-500" />
                                    <span className="text-xs text-blue-600">Read</span>
                                  </div>
                                );
                              }
                              if (ds === 'delivered') {
                                return (
                                  <div className="flex items-center gap-1" title="Delivered">
                                    <CheckCheck size={14} className="text-emerald-500" />
                                    <span className="text-xs text-emerald-600">Delivered</span>
                                  </div>
                                );
                              }
                              if (ds === 'failed') {
                                return (
                                  <div className="flex items-center gap-1" title={msg.error_message || 'Failed'}>
                                    <AlertTriangle size={14} className="text-red-500" />
                                    <span className="text-xs text-red-600">
                                      Failed{msg.sms_fallback_sent ? ' → SMS sent' : ''}
                                    </span>
                                  </div>
                                );
                              }
                              return (
                                <div className="flex items-center gap-1" title="Sent">
                                  <Check size={14} className="text-zinc-400" />
                                  <span className="text-xs text-zinc-500">Sent</span>
                                </div>
                              );
                            }
                            if (msg.type === 'sms') {
                              // SMS: Single green tick for delivered
                              if (msg.delivery_status === 'delivered' || msg.delivered_at) {
                                return (
                                  <div className="flex items-center gap-1" title="Delivered">
                                    <Check size={14} className="text-emerald-500" />
                                    <span className="text-xs text-emerald-600">Delivered</span>
                                  </div>
                                );
                              }
                              return (
                                <div className="flex items-center gap-1" title="Sent">
                                  <Check size={14} className="text-zinc-400" />
                                  <span className="text-xs text-zinc-500">Sent</span>
                                </div>
                              );
                            } else {
                              // Email: Double green tick for opened
                              if (msg.delivery_status === 'opened' || msg.opened_at) {
                                return (
                                  <div className="flex items-center gap-1" title="Opened">
                                    <CheckCheck size={14} className="text-emerald-500" />
                                    <span className="text-xs text-emerald-600">Opened</span>
                                  </div>
                                );
                              }
                              if (msg.delivery_status === 'delivered' || msg.delivered_at) {
                                return (
                                  <div className="flex items-center gap-1" title="Delivered">
                                    <Check size={14} className="text-emerald-500" />
                                    <span className="text-xs text-emerald-600">Delivered</span>
                                  </div>
                                );
                              }
                              if (msg.delivery_status === 'bounced') {
                                return (
                                  <div className="flex items-center gap-1" title="Bounced">
                                    <AlertTriangle size={14} className="text-red-500" />
                                    <span className="text-xs text-red-600">Bounced</span>
                                  </div>
                                );
                              }
                              return (
                                <div className="flex items-center gap-1" title="Sent">
                                  <Check size={14} className="text-zinc-400" />
                                  <span className="text-xs text-zinc-500">Sent</span>
                                </div>
                              );
                            }
                          };

                          // Header colours: WhatsApp = lime/teal, Email = blue, SMS = emerald
                          const headerBg = isWhatsApp
                            ? 'bg-[#25d366]/10'
                            : isEmail
                              ? 'bg-blue-50'
                              : 'bg-emerald-50';
                          const chipBg = isWhatsApp
                            ? 'bg-[#25d366]/20 text-[#075e54]'
                            : isEmail
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-emerald-100 text-emerald-700';
                          const channelLabel = isWhatsApp ? 'WhatsApp' : isEmail ? 'Email' : 'SMS';
                          const directionLabel = msg.direction === 'inbound'
                            ? `${channelLabel} from ${customer?.name?.split(' ')[0] || 'customer'}`
                            : `${channelLabel} sent by ${msg.sender_name || 'You'}`;

                          return (
                            <div key={msg.id || msgIndex} className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid={`message-${msg.id}`}>
                              {/* Message Header */}
                              <div className={`px-4 py-2 flex items-center justify-between ${headerBg}`}>
                                <div className="flex items-center gap-2">
                                  {isWhatsApp ? (
                                    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-[#25d366]" fill="currentColor" aria-hidden="true">
                                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                                    </svg>
                                  ) : isEmail ? (
                                    <Mail size={14} className="text-blue-600" />
                                  ) : (
                                    <MessageSquare size={14} className="text-emerald-600" />
                                  )}
                                  <span className="text-sm font-medium text-zinc-700">
                                    {directionLabel}
                                  </span>
                                  {msg.template_name && (
                                    <span className="text-xs px-1.5 py-0.5 rounded bg-white/70 text-zinc-600 border border-zinc-200">
                                      {msg.template_name}
                                    </span>
                                  )}
                                </div>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${chipBg}`}>
                                  {channelLabel}
                                </span>
                              </div>
                              
                              {/* Message Content */}
                              <div className="p-4">
                                {isEmail && msg.subject && (
                                  <p className="font-medium text-zinc-900 mb-2">{msg.subject}</p>
                                )}
                                <p className="text-sm text-zinc-600 whitespace-pre-wrap">
                                  {msg.content || msg.body || msg.message}
                                </p>
                              </div>
                              
                              {/* Message Footer - Timestamp and Delivery Status */}
                              <div className="px-4 py-2 bg-zinc-50 flex items-center justify-between gap-2 flex-wrap">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-xs text-zinc-500">
                                    {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString('en-GB', { 
                                      hour: '2-digit', minute: '2-digit' 
                                    }) : ''}
                                  </span>
                                  {/* Provider chip — visible only for SMS sends, surfaces the
                                      gateway that actually delivered the message. Lets users
                                      see at a glance whether a chase went via ClickSend
                                      (their preferred gateway) or fell back to Twilio. */}
                                  {msg.type === 'sms' && msg.provider && (
                                    <span
                                      className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full font-medium ${
                                        msg.provider === 'clicksend'
                                          ? 'bg-blue-100 text-blue-700'
                                          : 'bg-zinc-200 text-zinc-600'
                                      }`}
                                      title={`Sent via ${msg.provider}`}
                                      data-testid={`sms-provider-${msg.id}`}
                                    >
                                      {msg.provider === 'clicksend' ? 'ClickSend' : 'Twilio'}
                                    </span>
                                  )}
                                  {msg.type === 'sms' && msg.cost > 0 && (
                                    <span className="text-[10px] text-zinc-500" title="SMS cost">
                                      ${msg.cost.toFixed(4)}
                                    </span>
                                  )}
                                </div>
                                {renderDeliveryStatus()}
                              </div>
                              {msg.type === 'sms' && msg.error_message && (
                                <div className="px-4 py-2 bg-rose-50 border-t border-rose-200 text-xs text-rose-700 flex items-start gap-1.5">
                                  <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
                                  <span>{msg.error_message}</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Send SMS, WhatsApp & Email Buttons */}
              <div className="bg-white rounded-xl p-3 shadow-sm space-y-2">
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => setShowSMSCompose(true)}
                    className="flex flex-col items-center justify-center gap-1 px-3 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-medium transition-colors"
                    data-testid="compose-sms-btn"
                  >
                    <MessageSquare size={18} />
                    <span className="text-xs">SMS</span>
                  </button>
                  <button
                    onClick={() => setShowWaCompose(true)}
                    disabled={!customer?.phone}
                    className="flex flex-col items-center justify-center gap-1 px-3 py-3 bg-[#25d366] hover:bg-[#1ebe5b] disabled:bg-zinc-300 disabled:cursor-not-allowed text-white rounded-xl font-medium transition-colors"
                    data-testid="compose-whatsapp-btn"
                    title={customer?.phone ? 'Send WhatsApp template' : 'No phone number on file'}
                  >
                    <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                    <span className="text-xs">WhatsApp</span>
                  </button>
                  <button
                    onClick={() => setShowEmailCompose(true)}
                    disabled={!customer?.email}
                    className="flex flex-col items-center justify-center gap-1 px-3 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-300 disabled:cursor-not-allowed text-white rounded-xl font-medium transition-colors"
                    data-testid="compose-email-btn"
                  >
                    <Mail size={18} />
                    <span className="text-xs">Email</span>
                  </button>
                </div>
                <p className="text-xs text-zinc-400 text-center">
                  {customer?.phone && customer?.email ? `${customer.phone} | ${customer.email}` :
                   customer?.phone ? customer.phone :
                   customer?.email ? customer.email :
                   'No contact info on file'}
                </p>
              </div>
            </div>
          )}
          
          {activeTab === 'times' && (
            <div className="p-4">
              <h3 className="text-lg font-semibold text-white mb-4">Job Times History</h3>
              {loadingJobTimes ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 size={32} className="animate-spin text-emerald-500" />
                </div>
              ) : jobTimes.length === 0 ? (
                <div className="text-center py-12">
                  <Clock size={48} className="mx-auto mb-4 text-zinc-600" />
                  <p className="text-zinc-400">No job time data yet</p>
                  <p className="text-xs text-zinc-500 mt-2">
                    Times are recorded when workers share their location
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {jobTimes.map((job, idx) => (
                    <div key={job.id || idx} className="bg-zinc-800 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-zinc-400">{job.date}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          job.status === 'completed' || job.status === 'done'
                            ? 'bg-emerald-500/20 text-emerald-400'
                            : 'bg-zinc-700 text-zinc-400'
                        }`}>
                          {job.status || 'scheduled'}
                        </span>
                      </div>
                      <p className="text-white font-medium">{job.service}</p>
                      <p className="text-sm text-zinc-400">{job.worker}</p>
                      
                      <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-zinc-700">
                        <div className="text-center">
                          <p className="text-xs text-zinc-500">Arrived</p>
                          <p className={`text-sm font-medium ${job.arrived_at ? 'text-blue-400' : 'text-zinc-600'}`}>
                            {job.arrived_at ? new Date(job.arrived_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '-'}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-zinc-500">Left</p>
                          <p className={`text-sm font-medium ${job.departed_at ? 'text-orange-400' : 'text-zinc-600'}`}>
                            {job.departed_at ? new Date(job.departed_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '-'}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-zinc-500">On Site</p>
                          <p className={`text-sm font-semibold ${job.time_on_site_minutes ? 'text-emerald-400' : 'text-zinc-600'}`}>
                            {job.time_on_site_minutes ? `${job.time_on_site_minutes}m` : '-'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {activeTab === 'alerts' && (
            <div className="p-8 text-center text-gray-500">
              <Bell size={48} className="mx-auto mb-4 text-gray-300" />
              <p>No alerts</p>
            </div>
          )}
          </div>
          
          {/* FAB Menu - Centered above FAB button */}
          {showFabMenu && (
            <div className="fixed bottom-20 left-1/2 transform -translate-x-1/2 bg-white rounded-2xl shadow-xl z-[110] overflow-hidden min-w-[200px]">
              <button
                onClick={() => {
                  setShowFabMenu(false);
                  setShowAddJobModal(true);
                }}
                className="w-full px-4 py-3 flex items-center gap-3 hover:bg-zinc-50 text-left border-b border-zinc-100"
              >
                <Briefcase size={20} className="text-emerald-500" />
                <span>Add New Job</span>
              </button>
              {customerJobs.length > 0 && (
                <button
                  onClick={() => {
                    setShowFabMenu(false);
                    // Edit the first job (most relevant one)
                    handleEditJob(customerJobs[0]);
                  }}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-zinc-50 text-left border-b border-zinc-100"
                >
                  <Edit3 size={20} className="text-amber-500" />
                  <span>Edit Job</span>
                </button>
              )}
              <button
                onClick={() => {
                  setShowFabMenu(false);
                  setShowEditForm(true);
                }}
                className="w-full px-4 py-3 flex items-center gap-3 hover:bg-zinc-50 text-left border-b border-zinc-100"
              >
                <Edit2 size={20} className="text-blue-500" />
                <span>Edit Customer</span>
              </button>
              <button
                onClick={() => {
                  setShowFabMenu(false);
                  setShowLocationPicker(true);
                }}
                className="w-full px-4 py-3 flex items-center gap-3 hover:bg-zinc-50 text-left border-b border-zinc-100"
              >
                <MapPin size={20} className="text-orange-500" />
                <span>Change Location</span>
              </button>
              <button
                onClick={() => {
                  setShowFabMenu(false);
                  // Navigate to payment setup
                  window.location.href = `/payment-setup/${customer.id}`;
                }}
                className="w-full px-4 py-3 flex items-center gap-3 hover:bg-zinc-50 text-left"
              >
                <CreditCard size={20} className="text-purple-500" />
                <span>Setup Payment</span>
              </button>
            </div>
          )}
          
          {/* FAB Button - Centered at bottom, smaller */}
          <button 
            onClick={() => setShowFabMenu(!showFabMenu)}
            className={`fixed bottom-6 left-1/2 transform -translate-x-1/2 w-11 h-11 rounded-full shadow-lg flex items-center justify-center text-white z-[105] transition-all ${showFabMenu ? 'bg-zinc-800 rotate-45' : 'bg-emerald-500'}`}
          >
            {showFabMenu ? <X size={20} /> : <MoreVertical size={20} />}
          </button>
        </div>
      </div>
      
      {/* Job Detail Panel — the ONE job modal, same as Work Planner/Rounds */}
      {selectedJob && (
        <JobDetailPanel
          job={selectedJob}
          onClose={() => setSelectedJob(null)}
          showActions={false}
          zIndex={112}
          onViewCustomer={() => setSelectedJob(null)}
          onUpdateJob={async (jobId, updates) => {
            try {
              const token = localStorage.getItem('streakfree_token');
              if (updates._customer_zone !== undefined) {
                await fetch(`${API_URL}/api/customers/${selectedJob.customer_id || customer.id}`, {
                  method: 'PATCH',
                  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                  body: JSON.stringify({ zone: updates._customer_zone }),
                });
                updates = { ...updates };
                delete updates._customer_zone;
                delete updates.round_name;
                if (!Object.keys(updates).length) {
                  setJobsRefreshTick(t => t + 1);
                  return;
                }
              }
              const response = await fetch(`${API_URL}/api/jobs/${jobId}`, {
                method: 'PATCH',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(updates),
              });
              if (response.ok) {
                const updated = await response.json();
                setSelectedJob(prev => prev ? { ...prev, ...updated } : prev);
                setJobsRefreshTick(t => t + 1);
                localStorage.setItem('lastJobUpdate', Date.now().toString());
              }
            } catch (err) {
              console.error('Error updating job from customer profile:', err);
            }
          }}
          onUndoComplete={async (job) => {
            try {
              const token = localStorage.getItem('streakfree_token');
              const response = await fetch(`${API_URL}/api/jobs/${job.id}/undo-complete`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
              });
              if (response.ok) {
                setSelectedJob(prev => prev ? { ...prev, status: 'scheduled' } : prev);
                setJobsRefreshTick(t => t + 1);
              }
            } catch (err) {
              console.error('Error undoing complete:', err);
            }
          }}
          onViewInvoice={(inv) => setInvoiceForModal(inv)}
        />
      )}

      {/* Invoice summary — view/resend, same modal as Work Planner */}
      {invoiceForModal && (
        <InvoiceSummaryModal
          invoice={invoiceForModal}
          zIndex={116}
          onClose={() => setInvoiceForModal(null)}
          onSendInvoice={async (invoice, method) => {
            try {
              const token = localStorage.getItem('streakfree_token');
              const response = await fetch(`${API_URL}/api/invoices/${invoice.id}/send?method=${method}&copy_to_owner=true`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
              });
              if (response.ok) {
                toast.success(`Invoice sent via ${method} (copy to you)`);
              } else {
                const err = await response.json();
                toast.error(err.detail || `Failed to send ${method}`);
              }
            } catch (err) {
              toast.error(`Failed to send invoice via ${method}`);
            }
          }}
        />
      )}
      
      {/* Job Edit Modal */}
      {editingJob && (
        <JobEditModal
          job={editingJob}
          onClose={() => setEditingJob(null)}
          onSave={handleJobSave}
          onDelete={(job) => {
            handleDeleteJob(job);
            setEditingJob(null);
          }}
        />
      )}
      
      {/* Add Job Modal */}
      {showAddJobModal && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[110] flex items-center justify-center p-4 safe-area-all"
          onClick={() => setShowAddJobModal(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md modal-shell-h overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-6 py-5 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-xl">
                    <Briefcase size={24} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">Add Job</h2>
                    <p className="text-white/80 text-sm">for {customer.name}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowAddJobModal(false)}
                  className="p-2 hover:bg-white/20 rounded-xl transition-colors"
                >
                  <X size={22} />
                </button>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              {jobError && (
                <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl text-sm">
                  {jobError}
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-zinc-700 mb-2">Services</label>
                <div className="space-y-2">
                  {/* Selected services tags */}
                  <div className="flex flex-wrap gap-2 min-h-[44px] p-2 bg-zinc-50 border border-zinc-200 rounded-xl">
                    {(newJobService || '').split(',').filter(s => s.trim()).map((svc, idx) => (
                      <span 
                        key={idx}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-lg text-sm"
                      >
                        {svc.trim()}
                        <button
                          type="button"
                          onClick={() => {
                            const currentServices = (newJobService || '').split(',').filter(s => s.trim());
                            const newServices = currentServices.filter(s => s.trim() !== svc.trim());
                            setNewJobService(newServices.join(', '));
                          }}
                          className="hover:text-emerald-900"
                        >
                          <X size={14} />
                        </button>
                      </span>
                    ))}
                    {!(newJobService || '').trim() && (
                      <span className="text-zinc-400 text-sm py-1">No services selected</span>
                    )}
                  </div>
                  {/* Service dropdown to add. The "Add new…" option lets
                      the user type a one-off service name without having
                      to leave the modal and go to Settings → Services. */}
                  <select
                    value=""
                    onChange={(e) => {
                      const val = e.target.value;
                      if (!val) return;
                      let svcName = val;
                      if (val === '__custom__') {
                        const typed = (window.prompt('New service name:') || '').trim();
                        if (!typed) return;
                        svcName = typed;
                        // Fire-and-forget: persist to the user's saved
                        // Services so it appears in the dropdown next time.
                        // We don't await — the chip is added immediately
                        // either way, and the service list updates async.
                        persistCustomService(typed, availableServices).then(saved => {
                          if (saved) setAvailableServices(prev => [...prev, saved]);
                        });
                      }
                      const currentServices = (newJobService || '').split(',').filter(s => s.trim());
                      if (!currentServices.includes(svcName)) {
                        const newServices = [...currentServices, svcName];
                        setNewJobService(newServices.join(', '));
                      }
                    }}
                    className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    data-testid="customer-job-service-select"
                  >
                    <option value="">+ Add a service</option>
                    {availableServices.map(service => (
                      <option key={service.id} value={service.name}>
                        {service.name}
                      </option>
                    ))}
                    <option value="__custom__">+ Add new (type your own)…</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-zinc-700 mb-2">Job Address</label>
                <div className="relative">
                  <MapPin size={18} className="absolute left-4 top-4 text-zinc-400" />
                  <textarea
                    value={newJobAddress}
                    onChange={(e) => setNewJobAddress(e.target.value)}
                    placeholder="Enter job address"
                    rows={2}
                    className="w-full pl-11 pr-4 py-3 bg-zinc-50 border text-zinc-900 border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                  />
                </div>
                <p className="text-xs text-zinc-500 mt-1">Auto-filled from customer address. Edit if job is at a different location.</p>
              </div>

              {/* Services & Price — Squeegee-style tabbed picker. Mirrors
                  NewJobModal & JobEditModal so all three Add Job entry
                  points behave identically. */}
              <div>
                <label className="block text-sm font-semibold text-zinc-700 mb-2">Price</label>
                <div className="flex bg-zinc-100 rounded-xl p-1 mb-3" role="tablist">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={newJobPricingMode !== 'lines'}
                    onClick={() => setNewJobPricingMode('flat')}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      newJobPricingMode !== 'lines' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
                    }`}
                    data-testid="cust-newjob-pricing-mode-flat"
                  >
                    Price Job
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={newJobPricingMode === 'lines'}
                    onClick={() => {
                      if (newJobLineItems.length === 0) {
                        const svcs = (newJobService || '').split(',').map(s => s.trim()).filter(Boolean);
                        const seeded = svcs.length > 0
                          ? svcs.map(s => ({ service: s, rate: parseFloat(newJobPrice) || 0, quantity: 1 }))
                          : [{ service: '', rate: parseFloat(newJobPrice) || 0, quantity: 1 }];
                        setNewJobLineItems(seeded);
                      }
                      setNewJobPricingMode('lines');
                    }}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      newJobPricingMode === 'lines' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
                    }`}
                    data-testid="cust-newjob-pricing-mode-lines"
                  >
                    Price Services
                  </button>
                </div>

                {newJobPricingMode === 'lines' ? (
                  <div className="space-y-2" data-testid="cust-newjob-line-items-editor">
                    {newJobLineItems.map((li, idx) => {
                      const lineTotal = (parseFloat(li.rate) || 0) * (parseFloat(li.quantity) || 1);
                      return (
                        <div key={idx} className="bg-zinc-50 rounded-xl p-3 space-y-2">
                          <div className="flex items-start gap-2">
                            <select
                              value={li.service}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === '__custom__') {
                                  const typed = (window.prompt('New service name:') || '').trim();
                                  if (!typed) return;
                                  persistCustomService(typed, availableServices).then(saved => {
                                    if (saved) setAvailableServices(prev => [...prev, saved]);
                                  });
                                  const next = [...newJobLineItems];
                                  next[idx] = { ...next[idx], service: typed };
                                  setNewJobLineItems(next);
                                  return;
                                }
                                const next = [...newJobLineItems];
                                const svc = availableServices.find(s => s.name === val);
                                next[idx] = {
                                  ...next[idx],
                                  service: val,
                                  rate: (!next[idx].rate && svc?.default_price)
                                    ? Number(svc.default_price) : next[idx].rate,
                                };
                                setNewJobLineItems(next);
                              }}
                              className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-zinc-300 text-sm focus:border-emerald-500 outline-none"
                            >
                              <option value="">Choose service…</option>
                              {availableServices.map(s => (
                                <option key={s.id} value={s.name}>{s.name}</option>
                              ))}
                              {li.service && !availableServices.find(s => s.name === li.service) && (
                                <option value={li.service}>{li.service}</option>
                              )}
                              <option value="__custom__">+ Add new (type your own)…</option>
                            </select>
                            <button
                              type="button"
                              onClick={() => setNewJobLineItems(newJobLineItems.filter((_, i) => i !== idx))}
                              className="p-2 text-zinc-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              aria-label="Remove line"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                          <div className="grid grid-cols-3 gap-2 items-end">
                            <div>
                              <label className="block text-[11px] text-zinc-500 mb-1">Rate (£)</label>
                              <input
                                type="number" step="0.01" min="0"
                                value={li.rate}
                                onChange={(e) => {
                                  const next = [...newJobLineItems];
                                  next[idx] = { ...next[idx], rate: e.target.value };
                                  setNewJobLineItems(next);
                                }}
                                className="w-full px-2 py-1.5 rounded-lg border border-zinc-300 text-sm focus:border-emerald-500 outline-none"
                              />
                            </div>
                            <div>
                              <label className="block text-[11px] text-zinc-500 mb-1">Qty</label>
                              <input
                                type="number" step="1" min="1"
                                value={li.quantity}
                                onChange={(e) => {
                                  const next = [...newJobLineItems];
                                  next[idx] = { ...next[idx], quantity: e.target.value };
                                  setNewJobLineItems(next);
                                }}
                                className="w-full px-2 py-1.5 rounded-lg border border-zinc-300 text-sm focus:border-emerald-500 outline-none"
                              />
                            </div>
                            <div className="text-right">
                              <label className="block text-[11px] text-zinc-500 mb-1">Total</label>
                              <div className="font-semibold text-zinc-900 text-sm py-1.5">
                                £{lineTotal.toFixed(2)}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <button
                      type="button"
                      onClick={() => setNewJobLineItems([...newJobLineItems, { service: '', rate: 0, quantity: 1 }])}
                      className="w-full py-2 border-2 border-dashed border-zinc-300 rounded-xl text-sm font-medium text-zinc-600 hover:border-emerald-400 hover:text-emerald-600 transition-colors"
                    >
                      + Add service
                    </button>
                    <BulkUnitAdder
                      onAdd={(lines) => setNewJobLineItems(prev => [...prev.filter(li => (li.service || '').trim()), ...lines])}
                    />
                    <div className="flex justify-between items-center p-2 bg-emerald-50 rounded-lg border border-emerald-200">
                      <span className="font-semibold text-emerald-700">Total</span>
                      <span className="font-bold text-emerald-700" data-testid="cust-newjob-lines-total">
                        £{newJobLineItems.reduce((s, li) => s + (parseFloat(li.rate) || 0) * (parseFloat(li.quantity) || 1), 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="relative">
                    <PoundSterling size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newJobPrice}
                      onChange={(e) => setNewJobPrice(e.target.value)}
                      placeholder="0.00"
                      className="w-full pl-11 pr-4 py-3 bg-zinc-50 border text-zinc-900 border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-zinc-700 mb-2">Frequency</label>
                <select
                  value={newJobFrequency}
                  onChange={(e) => setNewJobFrequency(e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="6">6 Weekly</option>
                  <option value="12">12 Weekly</option>
                  <option value="0">One-off</option>
                </select>
              </div>

              <DaySuggestions
                customerId={customer?.id}
                address={newJobAddress || customer?.address}
                value={newJobDate}
                onPick={(d) => setNewJobDate(d)}
              />

              <div>
                <label className="block text-sm font-semibold text-zinc-700 mb-2">
                  {newJobFrequency === '0' ? 'Job Date *' : 'First Clean Date'}
                </label>
                <div className="relative">
                  <Calendar size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <input
                    type="date"
                    value={newJobDate}
                    onChange={(e) => setNewJobDate(e.target.value)}
                    className="w-full pl-11 pr-4 py-3 bg-zinc-50 border text-zinc-900 border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                {newJobFrequency === '0' ? (
                  <p className="text-xs text-amber-600 mt-1">One-off job - will only appear on this date</p>
                ) : (
                  <p className="text-xs text-zinc-500 mt-1">Leave blank to add to schedule later</p>
                )}
              </div>

              {/* Notes for invoice — customer-facing. Appears on the
                  invoice PDF, email, and public invoice page. Separate
                  from any internal notes (handled by job-detail panel). */}
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-zinc-700 mb-2">
                  <FileText size={16} className="text-amber-500" />
                  Notes for invoice
                  <span className="text-[10px] uppercase tracking-wider text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">Customer-facing</span>
                </label>
                <textarea
                  value={newJobInvoiceNotes}
                  onChange={(e) => setNewJobInvoiceNotes(e.target.value)}
                  rows={2}
                  placeholder="e.g. Cleaned inside conservatory glass · Next visit due 6 weeks"
                  className="w-full px-4 py-3 bg-zinc-50 border text-zinc-900 border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                  data-testid="cust-newjob-invoice-notes-input"
                />
                <p className="text-[11px] text-zinc-400 mt-1">Will appear on the invoice the customer receives.</p>
              </div>
            </div>

            <div className="px-6 pb-6 pt-3 flex gap-3 flex-shrink-0 border-t border-zinc-100">
              <button
                onClick={() => setShowAddJobModal(false)}
                data-testid="cust-newjob-cancel-btn"
                className="flex-1 py-3 border border-zinc-200 rounded-xl font-medium text-zinc-600 hover:bg-zinc-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateJob}
                data-testid="cust-newjob-submit-btn"
                disabled={savingJob || (newJobPricingMode === 'lines'
                  ? newJobLineItems.reduce((s, li) => s + (parseFloat(li.rate) || 0) * (parseFloat(li.quantity) || 1), 0) <= 0
                  : !newJobPrice)}
                className="flex-1 py-3 bg-emerald-500 text-white rounded-xl font-medium hover:bg-emerald-600 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {savingJob ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Check size={18} />
                    Add Job
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Take Payment Modal */}
      <TakePaymentModal
        isOpen={showTakePayment}
        onClose={() => setShowTakePayment(false)}
        customer={{
          id: customer?.id,
          name: customer?.name,
          balance: localBalance
        }}
        job={customerJobs[0] ? {
          id: customerJobs[0].id,
          address: customerJobs[0].address,
          price: customerJobs[0].price
        } : null}
        onSuccess={(data) => {
          if (data?.customer?.new_balance !== undefined) {
            setLocalBalance(data.customer.new_balance);
          }
          if (onRefresh) onRefresh();
        }}
      />
      
      {/* SMS Compose Modal */}
      <SMSComposeModal
        isOpen={showSMSCompose}
        onClose={(success) => {
          setShowSMSCompose(false);
          if (success) {
            fetchCombinedMessages();
          }
        }}
        recipients={[{
          id: customer?.id,
          customer_id: customer?.id,
          name: customer?.name,
          phone: customer?.phone
        }]}
        context={{
          service: customerJobs[0]?.service || customerJobs[0]?.services || 'service',
          amount: customerJobs[0]?.price,
          balance: localBalance
        }}
      />
      
      {/* Email Compose Modal */}
      <EmailComposeModal
        isOpen={showEmailCompose}
        onClose={() => setShowEmailCompose(false)}
        recipient={{
          id: customer?.id,
          customer_id: customer?.id,
          name: customer?.name,
          email: customer?.email,
          address: customer?.address
        }}
        context={{
          service: customerJobs[0]?.services || customerJobs[0]?.service || 'service',
          amount: customerJobs[0]?.price,
          balance: localBalance,
          business_name: 'Perfect Panes' // This should come from settings
        }}
        onEmailSent={() => {
          fetchCombinedMessages();
        }}
      />

      {/* WhatsApp Compose Modal */}
      <WhatsAppComposeModal
        isOpen={showWaCompose}
        onClose={(success) => {
          setShowWaCompose(false);
          if (success) {
            fetchCombinedMessages();
          }
        }}
        recipient={waRecipient}
        defaultTemplate={localBalance > 0 ? 'payment_reminder' : 'general_message'}
      />
      
      {/* Address Picker Modal */}
      {showLocationPicker && (
        <AddressPickerModal
          isOpen={showLocationPicker}
          onClose={() => setShowLocationPicker(false)}
          onSave={handleSaveLocation}
          initialAddress={customer?.address || ''}
          initialLat={customerLocation.lat || customer?.latitude}
          initialLng={customerLocation.lng || customer?.longitude}
        />
      )}
    </>,
    document.body
  );
};

// FAB Menu Component
const FabMenu = ({ isOpen, onToggle, onAddNew }) => {
  const menuItems = [
    { icon: Plus, color: '#10b981', label: 'Add New Customer', action: onAddNew },
    { icon: Users, color: '#f97316', label: 'Merge Customers' },
    { icon: Upload, color: '#8b5cf6', label: 'Import Customers' },
    { icon: RotateCcw, color: '#3b82f6', label: 'Restore Deleted' },
  ];

  return (
    <div className="fixed bottom-20 right-6 z-40">
      {isOpen && (
        <div className="absolute bottom-16 right-0 flex flex-col gap-3 mb-2">
          {menuItems.map((item, index) => (
            <button
              key={index}
              onClick={item.action}
              className="w-12 h-12 rounded-full shadow-lg flex items-center justify-center text-white transition-transform hover:scale-110"
              style={{ backgroundColor: item.color }}
              title={item.label}
            >
              <item.icon size={20} />
            </button>
          ))}
        </div>
      )}
      
      <button
        data-testid="customers-fab"
        onClick={onToggle}
        className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center text-white transition-all ${
          isOpen ? 'bg-emerald-500 rotate-45' : 'bg-emerald-500'
        }`}
      >
        {isOpen ? <X size={24} /> : <Plus size={24} />}
      </button>
    </div>
  );
};

const Customers = ({ initialCustomer, onBack, onSelectCustomer, onNavigateToWorkPlanner, onNavigate }) => {
  const [searchInput, setSearchInput] = useState(''); // What user types
  const [searchQuery, setSearchQuery] = useState(''); // Actual search query (debounced)
  const [selectedCustomer, setSelectedCustomer] = useState(initialCustomer || null);
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  // After a new customer is saved, prompt the user to add a job for them.
  // When user clicks Yes on the prompt, open CustomerDetailModal with an
  // autoOpen signal so the Add Job sub-modal pops immediately.
  const [autoOpenAddJobFor, setAutoOpenAddJobFor] = useState(null);
  const [showCSVImport, setShowCSVImport] = useState(false);
  const [fabOpen, setFabOpen] = useState(false);
  const [filterTime, setFilterTime] = useState('all');
  const [filterStatus, setFilterStatus] = useState('active'); // 'active', 'inactive', 'all'
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' = A-Z, 'desc' = Z-A, 'recent' = Recently Added
  const PAGE_SIZE = 1000; // Load all customers
  
  // Invoice modal state (for viewing invoices from various places)
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  
  // Bulk selection state
  const [selectedCustomerIds, setSelectedCustomerIds] = useState(new Set());
  const [showBulkReassign, setShowBulkReassign] = useState(false);
  const [showBulkMessage, setShowBulkMessage] = useState(false);
  const [showNotifyByDateModal, setShowNotifyByDateModal] = useState(false);
  const [showBulkMoveRound, setShowBulkMoveRound] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [roundsList, setRoundsList] = useState([]);
  const [newRoundName, setNewRoundName] = useState('');
  
  // Fetch rounds list
  useEffect(() => {
    const fetchRounds = async () => {
      try {
        const token = api.getToken();
        const res = await fetch(`${API_URL}/api/rounds`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          const roundNames = (data.rounds || data || []).map(r => r.name).filter(Boolean);
          setRoundsList(roundNames);
        }
      } catch (err) {
        console.error('Error fetching rounds:', err);
      }
    };
    fetchRounds();
  }, [API_URL]);
  
  // Fetch staff list for reassignment
  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const token = api.getToken();
        const res = await fetch(`${API_URL}/api/team/users`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          const workers = (data.users || [])
            .filter(u => u.role === 'Worker' || u.role === 'Planner')
            .map(u => u.name)
            .filter(Boolean);
          setStaffList(workers);
        }
      } catch (err) {
        console.error('Error fetching staff:', err);
      }
    };
    fetchStaff();
  }, []);
  
  // Toggle customer selection
  const toggleCustomerSelection = useCallback((customerId) => {
    setSelectedCustomerIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(customerId)) {
        newSet.delete(customerId);
      } else {
        newSet.add(customerId);
      }
      return newSet;
    });
  }, []);
  
  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedCustomerIds(new Set());
  }, []);

  // Check for filters/search from dashboard on mount
  useEffect(() => {
    const savedFilter = sessionStorage.getItem('customerFilter');
    if (savedFilter === 'this-week') {
      setFilterTime('this-week');
      sessionStorage.removeItem('customerFilter');
    }
    
    // Check for search query from header search
    const savedSearch = sessionStorage.getItem('customerSearchQuery');
    if (savedSearch) {
      setSearchInput(savedSearch);
      setSearchQuery(savedSearch);
      sessionStorage.removeItem('customerSearchQuery');
    }
    
    const openCreate = sessionStorage.getItem('openCreateForm');
    if (openCreate === 'true') {
      setShowNewCustomerForm(true);
      sessionStorage.removeItem('openCreateForm');
    }

    // Deep-link support: /customers?new=true opens the New Customer form
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('new') === 'true') {
        setShowNewCustomerForm(true);
        // Clear the param from the URL so refresh / back doesn't reopen it
        params.delete('new');
        const remaining = params.toString();
        const newUrl = window.location.pathname + (remaining ? `?${remaining}` : '') + window.location.hash;
        window.history.replaceState({}, '', newUrl);
      }
    } catch {
      /* no-op */
    }
  }, []);

  // Header "+ New → New Customer" dispatches this event. Listening here means
  // the form opens even when we're already on the Customers page (in which
  // case App.js's navigateToPage is a no-op and we'd otherwise miss it).
  useEffect(() => {
    const handler = (e) => {
      const action = e?.detail?.action;
      if (action !== 'customers') return;
      sessionStorage.removeItem('openCreateForm');
      setShowNewCustomerForm(true);
    };
    window.addEventListener('streakfree:open-create-form', handler);
    return () => window.removeEventListener('streakfree:open-create-form', handler);
  }, []);

  // Check for openCustomerId from notification click - auto-open customer card
  useEffect(() => {
    const openCustomerId = sessionStorage.getItem('openCustomerId');
    if (openCustomerId && !isLoading) {
      // Find the customer in the loaded list
      const customerToOpen = customers.find(c => c.id === openCustomerId);
      if (customerToOpen) {
        setSelectedCustomer(customerToOpen);
        sessionStorage.removeItem('openCustomerId');
      } else {
        // Customer not in current list - fetch it directly
        const fetchAndOpenCustomer = async () => {
          try {
            const token = api.getToken();
            const res = await fetch(`${API_URL}/api/customers/${openCustomerId}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
              const customer = await res.json();
              setSelectedCustomer(customer);
            }
          } catch (err) {
            console.error('Error fetching customer from notification:', err);
          } finally {
            sessionStorage.removeItem('openCustomerId');
          }
        };
        fetchAndOpenCustomer();
      }
    }
  }, [customers, isLoading]);

  // Listen for notification click event (when already on customers page)
  useEffect(() => {
    const handleNotificationEvent = async (e) => {
      const customerId = e.detail;
      if (customerId) {
        // Try to find in current list first
        const customerToOpen = customers.find(c => c.id === customerId);
        if (customerToOpen) {
          setSelectedCustomer(customerToOpen);
        } else {
          // Fetch directly
          try {
            const token = api.getToken();
            const res = await fetch(`${API_URL}/api/customers/${customerId}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
              const customer = await res.json();
              setSelectedCustomer(customer);
            }
          } catch (err) {
            console.error('Error fetching customer from notification:', err);
          }
        }
        sessionStorage.removeItem('openCustomerId');
      }
    };

    window.addEventListener('openCustomerFromNotification', handleNotificationEvent);
    return () => window.removeEventListener('openCustomerFromNotification', handleNotificationEvent);
  }, [customers]);

  // Debounce search input - only update actual search query after user stops typing
  useEffect(() => {
    // Don't trigger search on empty input immediately
    if (!searchInput) {
      setSearchQuery('');
      return;
    }
    
    const timeoutId = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 1200); // 1200ms debounce - wait until user stops typing
    
    return () => clearTimeout(timeoutId);
  }, [searchInput]);
  
  // Handle Enter key to search immediately
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSearchQuery(searchInput);
    }
  };
  
  // Refresh customers while preserving current view state (filters, search, etc.)
  const refreshCustomers = useCallback(() => {
    loadCustomers(0, false, searchQuery, filterStatus);
  }, [searchQuery, filterStatus]);

  // Fetch customers with pagination (with offline cache support)
  const loadCustomers = async (pageNum = 0, append = false, search = '', statusFilter = filterStatus, silent = false) => {
    // silent=true refreshes the list WITHOUT the full-screen spinner — the
    // spinner early-return unmounts any open modal (e.g. NewCustomerForm mid
    // "Add a Job" flow), which made the Add Customer form reappear blank.
    if (pageNum === 0) {
      if (!silent) setIsLoading(true);
    } else {
      setIsLoadingMore(true);
    }
    
    // If offline and this is the first page with no search, return cached data
    if (!isOnline() && pageNum === 0 && !search) {
      console.log('[Offline] Loading customers from cache');
      const cachedCustomers = getCachedCustomers();
      if (cachedCustomers.length > 0) {
        // Apply status filter to cached data
        let filteredCustomers = cachedCustomers;
        if (statusFilter === 'active') {
          filteredCustomers = cachedCustomers.filter(c => c.is_active !== false);
        } else if (statusFilter === 'inactive') {
          filteredCustomers = cachedCustomers.filter(c => c.is_active === false);
        }
        setCustomers(filteredCustomers);
        setTotalCount(filteredCustomers.length);
        setHasMore(false);
        setIsLoading(false);
        return;
      }
    }
    
    try {
      const token = api.getToken();
      const skip = pageNum * PAGE_SIZE;
      
      // Build query params
      let url = `${API_URL}/api/customers?limit=${PAGE_SIZE}&skip=${skip}`;
      if (search && search.trim()) {
        url += `&search=${encodeURIComponent(search.trim())}`;
      }
      if (filterTime === 'this-week') {
        url += `&created_after=${getWeekStart().toISOString()}`;
      }
      // Add status filter (active/inactive)
      if (statusFilter === 'active') {
        url += `&is_active=true`;
      } else if (statusFilter === 'inactive') {
        url += `&is_active=false`;
      }
      // If 'all', don't add filter
      
      console.log('Fetching customers:', url);
      
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        // Handle both array response and paginated response
        const customerList = Array.isArray(data) ? data : data.customers || [];
        const total = data.total || customerList.length;
        
        // Debug: log first customer to verify has_stripe field
        if (customerList.length > 0) {
          console.log('First customer data:', customerList[0].name, 'has_stripe:', customerList[0].has_stripe, 'has_gocardless:', customerList[0].has_gocardless);
        }
        
        if (append) {
          setCustomers(prev => [...prev, ...customerList]);
        } else {
          setCustomers(customerList);
          // Cache the first page of customers (no search filter) for offline use
          if (pageNum === 0 && !search && statusFilter !== 'inactive') {
            cacheCustomers(customerList);
            console.log('[Cache] Saved customers for offline access');
          }
        }
        
        setTotalCount(total);
        setHasMore(false); // No pagination - all loaded at once
      }
    } catch (err) {
      console.error('Error loading customers:', err);
      // On error, try to load from cache
      if (pageNum === 0 && !search) {
        const cachedCustomers = getCachedCustomers();
        if (cachedCustomers.length > 0) {
          console.log('[Offline] Loading customers from cache after fetch error');
          let filteredCustomers = cachedCustomers;
          if (statusFilter === 'active') {
            filteredCustomers = cachedCustomers.filter(c => c.is_active !== false);
          } else if (statusFilter === 'inactive') {
            filteredCustomers = cachedCustomers.filter(c => c.is_active === false);
          }
          setCustomers(filteredCustomers);
          setTotalCount(filteredCustomers.length);
          setHasMore(false);
        }
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  // Get start of week helper
  const getWeekStart = () => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() - diff);
    weekStart.setHours(0, 0, 0, 0);
    return weekStart;
  };

  // Load more customers on scroll
  const handleLoadMore = () => {
    if (!isLoadingMore && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadCustomers(nextPage, true, searchQuery);
    }
  };

  // Load customers when debounced search query changes
  useEffect(() => {
    setPage(0);
    loadCustomers(0, false, searchQuery, filterStatus);
  }, [searchQuery, filterTime, filterStatus]);

  // Subscribe to centralized data sync - auto refresh when jobs or customers change
  useEffect(() => {
    const unsubscribe = subscribeToRefresh('customers', (detail) => {
      console.log('[Customers] Refresh triggered by:', detail.source);
      loadCustomers(0, false, searchQuery, filterStatus, true);
    });
    return unsubscribe;
  }, [searchQuery]);
  
  // Sort customers based on sortOrder
  const sortedCustomers = useMemo(() => {
    const sorted = [...customers];
    
    if (sortOrder === 'recent') {
      // Sort by created_at descending (most recent first), limit to 25
      return sorted
        .sort((a, b) => {
          const dateA = new Date(a.created_at || 0);
          const dateB = new Date(b.created_at || 0);
          return dateB - dateA; // Descending (newest first)
        })
        .slice(0, 25);
    } else {
      // Alphabetical sort
      return sorted.sort((a, b) => {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        if (sortOrder === 'asc') {
          return nameA.localeCompare(nameB);
        } else {
          return nameB.localeCompare(nameA);
        }
      });
    }
  }, [customers, sortOrder]);
  
  // Select all visible customers
  const selectAllCustomers = useCallback(() => {
    const allIds = new Set(sortedCustomers.map(c => c.id));
    setSelectedCustomerIds(allIds);
  }, [sortedCustomers]);
  
  // Get selected customers data
  const selectedCustomers = useMemo(() => {
    return sortedCustomers.filter(c => selectedCustomerIds.has(c.id));
  }, [sortedCustomers, selectedCustomerIds]);

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-zinc-100">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-zinc-500">Loading customers...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="customers-page" className="h-full flex flex-col bg-zinc-100">
      {/* Simple Search Header */}
      <div className="p-4 bg-white border-b border-zinc-200">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-zinc-400" size={20} />
          <Input
            data-testid="customer-search"
            placeholder="Search customers... (press Enter or wait)"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            className="pl-12 h-14 rounded-xl border-zinc-200 text-lg focus:border-emerald-500 focus:ring-emerald-500/20"
          />
          {searchInput && (
            <button
              onClick={() => {
                setSearchInput('');
                setSearchQuery('');
              }}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
            >
              <X size={20} />
            </button>
          )}
        </div>
        
        {/* Show "Added This Week" filter if active */}
        {filterTime === 'this-week' && (
          <div className="mt-3">
            <button
              onClick={() => setFilterTime('all')}
              className="px-4 py-2 rounded-xl text-sm font-medium bg-emerald-500 text-white shadow-md flex items-center gap-2"
            >
              Added This Week
              <X size={14} />
            </button>
          </div>
        )}
        
        {/* Active/Inactive Filter Toggle + Sort Dropdown */}
        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setFilterStatus('active')}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                filterStatus === 'active' 
                  ? 'bg-emerald-500 text-white shadow-md' 
                  : 'bg-white text-zinc-600 border border-zinc-200 hover:bg-zinc-50'
              }`}
            >
              Active
            </button>
            <button
              onClick={() => setFilterStatus('inactive')}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                filterStatus === 'inactive' 
                  ? 'bg-amber-500 text-white shadow-md' 
                  : 'bg-white text-zinc-600 border border-zinc-200 hover:bg-zinc-50'
              }`}
            >
              Inactive
            </button>
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                filterStatus === 'all' 
                  ? 'bg-zinc-700 text-white shadow-md' 
                  : 'bg-white text-zinc-600 border border-zinc-200 hover:bg-zinc-50'
              }`}
            >
              All
            </button>
          </div>
          
          {/* Sort Dropdown */}
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            className="px-4 py-2 rounded-xl text-sm font-medium bg-white text-zinc-600 border border-zinc-200 hover:bg-zinc-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-500"
            data-testid="customer-sort-dropdown"
          >
            <option value="asc">A → Z</option>
            <option value="desc">Z → A</option>
            <option value="recent">Recently Added (25)</option>
          </select>
        </div>
      </div>
      
      {/* Stats bar */}
      <div className="px-4 py-3 bg-white border-b border-zinc-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users size={16} className="text-zinc-400" />
          <span className="text-sm font-medium text-zinc-900">
            {sortedCustomers.length} customers
          </span>
        </div>
        {searchQuery && (
          <span className="text-xs text-zinc-400">
            Searching: "{searchQuery}"
          </span>
        )}
      </div>
      
      {/* Bulk Action Bar - shows when customers are selected */}
      {selectedCustomerIds.size > 0 && (
        <div className="bg-zinc-900 text-white px-3 py-3 flex items-center gap-2 flex-wrap sm:flex-nowrap sm:justify-between">
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <button
              onClick={clearSelection}
              className="p-1.5 hover:bg-zinc-700 rounded-lg transition-colors shrink-0"
              data-testid="bulk-clear-selection-btn"
            >
              <X size={20} />
            </button>
            <span className="font-medium whitespace-nowrap">{selectedCustomerIds.size} selected</span>
            <button
              onClick={selectAllCustomers}
              className="text-xs text-zinc-400 hover:text-white underline shrink-0"
              data-testid="bulk-select-all-btn"
            >
              Select all ({sortedCustomers.length})
            </button>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto overflow-x-auto pb-1 sm:pb-0">
            <button
              onClick={() => setShowBulkMoveRound(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 rounded-lg text-sm font-medium transition-colors shrink-0"
              data-testid="bulk-move-round-btn"
            >
              <MapPin size={16} />
              Round
            </button>
            <button
              onClick={() => setShowBulkReassign(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-medium transition-colors shrink-0"
              data-testid="bulk-reassign-btn"
            >
              <UserPlus size={16} />
              Reassign
            </button>
            <button
              onClick={() => setShowBulkMessage(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-semibold transition-colors shrink-0"
              data-testid="bulk-message-btn"
            >
              <Send size={16} />
              Message
            </button>
          </div>
        </div>
      )}
      
      {/* Customer List */}
      <div className="flex-1 overflow-y-auto pt-4 pb-24">
        {sortedCustomers.map(customer => (
          <CustomerRow 
            key={customer.id} 
            customer={customer}
            onClick={() => setSelectedCustomer(customer)}
            isSelected={selectedCustomerIds.has(customer.id)}
            onToggleSelect={toggleCustomerSelection}
            onDateClick={(date) => onNavigateToWorkPlanner && onNavigateToWorkPlanner(date)}
          />
        ))}
        
        {/* Load More Button */}
        {hasMore && !isLoading && sortedCustomers.length > 0 && (
          <div className="py-4 px-4">
            <button
              onClick={handleLoadMore}
              disabled={isLoadingMore}
              className="w-full py-3 bg-zinc-100 hover:bg-zinc-200 rounded-xl text-zinc-700 font-medium transition-colors disabled:opacity-50"
            >
              {isLoadingMore ? 'Loading...' : 'Load More Customers'}
            </button>
          </div>
        )}
        
        {sortedCustomers.length === 0 && !isLoading && (
          <div className="flex flex-col items-center justify-center h-64 text-zinc-500">
            <div className="w-16 h-16 bg-zinc-200 rounded-2xl flex items-center justify-center mb-4">
              <User size={28} className="text-zinc-400" />
            </div>
            <p className="font-medium text-zinc-900">No customers found</p>
            <p className="text-sm text-zinc-500 mt-1">Try adjusting your search</p>
          </div>
        )}
        
        {/* Import & Ads Section */}
        <CustomerImportSection 
          onImportComplete={refreshCustomers}
          onNavigateToApiDocs={() => onNavigate && onNavigate('api-docs')}
        />
      </div>
      
      {/* Modern FAB - Centered at bottom */}
      <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 flex items-center gap-3 z-30">
        <button
          data-testid="customers-fab"
          onClick={() => setShowNewCustomerForm(true)}
          className="w-11 h-11 bg-emerald-500 text-white rounded-full shadow-lg shadow-emerald-500/30 flex items-center justify-center hover:bg-emerald-600 active:scale-95 transition-all"
        >
          <Plus size={22} strokeWidth={2.5} />
        </button>
        
        <button
          data-testid="csv-import-fab"
          onClick={() => setShowCSVImport(true)}
          className="w-11 h-11 bg-blue-500 text-white rounded-full shadow-lg shadow-blue-500/30 flex items-center justify-center hover:bg-blue-600 active:scale-95 transition-all"
          title="Import from CSV"
        >
          <Upload size={18} strokeWidth={2.5} />
        </button>

        <button
          data-testid="notify-customers-fab"
          onClick={() => {
            if (selectedCustomerIds.size > 0) {
              // Customers selected → message just them
              setShowBulkMessage(true);
            } else {
              // Nothing selected → date-range broadcast
              setShowNotifyByDateModal(true);
            }
          }}
          className="w-11 h-11 bg-indigo-600 text-white rounded-full shadow-lg shadow-indigo-600/30 flex items-center justify-center hover:bg-indigo-700 active:scale-95 transition-all relative"
          title={selectedCustomerIds.size > 0
            ? `Message ${selectedCustomerIds.size} selected customer${selectedCustomerIds.size === 1 ? '' : 's'}`
            : 'Notify customers by day (SMS / Email)'}
        >
          <Send size={18} strokeWidth={2.5} />
          {selectedCustomerIds.size > 0 && (
            <span
              data-testid="notify-fab-count-badge"
              className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-emerald-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-white"
            >
              {selectedCustomerIds.size}
            </span>
          )}
        </button>
      </div>

      {showNotifyByDateModal && (
        <NotifyCustomersModal
          apiUrl={API_URL}
          token={api.getToken()}
          onClose={() => setShowNotifyByDateModal(false)}
        />
      )}
      
      {selectedCustomer && (
        <CustomerDetailModal 
          customer={selectedCustomer}
          autoOpenAddJob={autoOpenAddJobFor === selectedCustomer.id}
          onClose={() => {
            setSelectedCustomer(null);
            setAutoOpenAddJobFor(null);
            // If we came from another page (initialCustomer was passed), go back to that page
            if (initialCustomer && onBack) {
              onBack();
            }
          }}
          onNavigateToDate={(date) => {
            setSelectedCustomer(null);
            if (onNavigateToWorkPlanner) {
              onNavigateToWorkPlanner(date);
            }
          }}
          onEditCustomer={(updatedCustomer) => {
            // Update customer in the list
            setCustomers(prev => prev.map(c => 
              c.id === updatedCustomer.id ? { ...c, ...updatedCustomer } : c
            ));
            // Update selected customer
            setSelectedCustomer(prev => prev ? { ...prev, ...updatedCustomer } : prev);
            // Notify Work Planner / Visual Planner / Schedule / Owings / Rounds
            // so jobs displayed on those pages refetch with the latest customer
            // name + address (job docs read from the live customer record on
            // /api/jobs, so a refetch is enough — no cascading needed).
            customerChanged(Actions.UPDATE, updatedCustomer);
          }}
          onStatusChange={async (customerId, newStatus) => {
            try {
              const token = localStorage.getItem('streakfree_token');
              const response = await fetch(`${API_URL}/api/customers/${customerId}`, {
                method: 'PUT',
                headers: { 
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({ is_active: newStatus })
              });
              if (response.ok) {
                const updatedCustomer = await response.json();
                // Update customer in the list
                setCustomers(prev => prev.map(c => 
                  c.id === customerId ? { ...c, is_active: newStatus } : c
                ));
                // Update selected customer
                setSelectedCustomer(prev => prev ? { ...prev, is_active: newStatus } : prev);
                // Inactive customers' jobs get hidden on Work Planner — fire
                // the event so it drops/restores them straight away.
                customerChanged(Actions.UPDATE, updatedCustomer);
              } else {
                const err = await response.json();
                alert(err.detail || 'Failed to update customer status');
              }
            } catch (err) {
              console.error('Error updating customer status:', err);
              alert('Error updating customer status');
            }
          }}
          onDeleteCustomer={async (customerId) => {
            try {
              const token = localStorage.getItem('streakfree_token');
              
              // Optimistic update - remove from UI immediately
              setCustomers(prev => prev.filter(c => c.id !== customerId));
              setTotalCount(prev => Math.max(0, prev - 1));
              setSelectedCustomer(null);
              
              const response = await fetch(`${API_URL}/api/customers/${customerId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
              });
              if (!response.ok) {
                const err = await response.json();
                // Revert on error - refresh the list
                refreshCustomers();
                alert(err.detail || 'Failed to delete customer');
                return;
              }
              // Notify every page that depends on customers/jobs (Work Planner,
              // Visual Planner, Schedule, Owings, Rounds, Reports, Overview…)
              // so they drop the deleted customer's jobs immediately instead
              // of waiting for the user to manually refresh that page.
              customerChanged(Actions.DELETE, { id: customerId });
            } catch (err) {
              console.error('Error deleting customer:', err);
              refreshCustomers();
              alert('Error deleting customer');
            }
          }}
        />
      )}
      
      {showNewCustomerForm && (
        <NewCustomerForm 
          onClose={() => setShowNewCustomerForm(false)}
          onSave={(newCustomer) => {
            // Clear active search/filter so the newly created customer is visible
            // (otherwise an active "julie" search hides them and the user thinks
            // the save failed).
            if (newCustomer?.id) {
              if (searchInput) setSearchInput('');
              if (searchQuery) setSearchQuery('');
              if (filterStatus !== 'active' && filterStatus !== 'all') setFilterStatus('all');
              setCustomers(prev => {
                if (prev.some(c => c.id === newCustomer.id)) return prev;
                return [newCustomer, ...prev];
              });
              setTotalCount(prev => prev + 1);
              toast.success(`✓ ${newCustomer.name} added`);
              // NewCustomerForm has its own "Add a Job" step after saving,
              // so no extra prompt here (was double-asking).
            }
            setShowNewCustomerForm(false);
          }}
        />
      )}
      
      {showCSVImport && (
        <CSVImport 
          onClose={() => setShowCSVImport(false)}
          onImportComplete={(count) => {
            setShowCSVImport(false);
            // Refresh customers list (preserving current view)
            refreshCustomers();
          }}
        />
      )}
      
      {/* Invoice View Modal */}
      {selectedInvoice && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedInvoice(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full modal-shell-h overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold text-lg">Invoice {selectedInvoice.invoice_number}</h3>
              <button onClick={() => setSelectedInvoice(null)} className="p-2 hover:bg-zinc-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Invoice Details */}
              <div className="bg-zinc-50 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-zinc-600">Amount</span>
                  <span className="font-bold text-xl">£{(selectedInvoice.amount || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-zinc-600">Status</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    selectedInvoice.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {selectedInvoice.status === 'paid' ? 'Paid' : 'Pending'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-zinc-600">Date</span>
                  <span className="text-zinc-900">{new Date(selectedInvoice.timestamp).toLocaleDateString('en-GB')}</span>
                </div>
              </div>
              
              {/* Send Options */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-zinc-700">Send Invoice</p>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={async () => {
                      setSendingInvoice(true);
                      try {
                        const token = localStorage.getItem('streakfree_token');
                        await fetch(`${API_URL}/api/invoices/${selectedInvoice.invoice_id}/send?method=sms&copy_to_owner=true`, {
                          method: 'POST',
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        setShowToast('SMS sent');
                        setTimeout(() => setShowToast(''), 3000);
                      } catch (err) {
                        console.error(err);
                      }
                      setSendingInvoice(false);
                    }}
                    disabled={sendingInvoice}
                    className="flex flex-col items-center gap-1 p-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                  >
                    <MessageSquare size={20} className="text-blue-600" />
                    <span className="text-xs font-medium text-blue-700">SMS</span>
                  </button>
                  <button
                    onClick={async () => {
                      setSendingInvoice(true);
                      try {
                        const token = localStorage.getItem('streakfree_token');
                        await fetch(`${API_URL}/api/invoices/${selectedInvoice.invoice_id}/send?method=email&copy_to_owner=true`, {
                          method: 'POST',
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        setShowToast('Email sent');
                        setTimeout(() => setShowToast(''), 3000);
                      } catch (err) {
                        console.error(err);
                      }
                      setSendingInvoice(false);
                    }}
                    disabled={sendingInvoice}
                    className="flex flex-col items-center gap-1 p-3 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors"
                  >
                    <Mail size={20} className="text-emerald-600" />
                    <span className="text-xs font-medium text-emerald-700">Email</span>
                  </button>
                  <button
                    onClick={async () => {
                      setSendingInvoice(true);
                      try {
                        const token = localStorage.getItem('streakfree_token');
                        await fetch(`${API_URL}/api/invoices/${selectedInvoice.invoice_id}/send?method=both&copy_to_owner=true`, {
                          method: 'POST',
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        setShowToast('SMS & Email sent');
                        setTimeout(() => setShowToast(''), 3000);
                      } catch (err) {
                        console.error(err);
                      }
                      setSendingInvoice(false);
                    }}
                    disabled={sendingInvoice}
                    className="flex flex-col items-center gap-1 p-3 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
                  >
                    <Send size={20} className="text-purple-600" />
                    <span className="text-xs font-medium text-purple-700">Both</span>
                  </button>
                </div>
              </div>
              
              {/* View Full Invoice Button */}
              {selectedInvoice.invoice_id && (
                <button
                  onClick={() => {
                    window.open(`${API_URL}/api/invoices/${selectedInvoice.invoice_id}/pdf`, '_blank');
                  }}
                  className="w-full py-3 bg-zinc-900 text-white rounded-lg font-medium hover:bg-zinc-800 transition-colors"
                >
                  View Full Invoice PDF
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Bulk Reassign Modal */}
      {showBulkReassign && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowBulkReassign(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold text-lg">Reassign {selectedCustomerIds.size} Customers</h3>
              <button onClick={() => setShowBulkReassign(false)} className="p-2 hover:bg-zinc-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <p className="text-sm text-zinc-600">Select a worker to reassign the selected customers to:</p>
              
              <div className="space-y-2">
                {staffList.map(worker => (
                  <button
                    key={worker}
                    onClick={async () => {
                      try {
                        const token = api.getToken();
                        // Get scope preference
                        const scope = document.querySelector('input[name="reassign-scope"]:checked')?.value || 'all';
                        
                        // Use bulk reassign endpoint
                        const customerIds = Array.from(selectedCustomerIds);
                        const response = await fetch(`${API_URL}/api/bulk/customers-reassign`, {
                          method: 'PUT',
                          headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                          },
                          body: JSON.stringify({
                            customer_ids: customerIds,
                            assignee: worker,
                            update_jobs: scope === 'all'
                          })
                        });
                        
                        if (response.ok) {
                          const result = await response.json();
                          toast.success(`Reassigned ${result.customers_updated} customers${scope === 'all' ? ` and ${result.jobs_updated} jobs` : ''} to ${worker}`);
                          setShowBulkReassign(false);
                          clearSelection();
                          refreshCustomers();
                          // Notify Work Planner / Schedule etc. so the new
                          // assignee colour + filter applies immediately.
                          customerChanged(Actions.BULK_UPDATE, { customer_ids: customerIds, assignee: worker });
                        } else {
                          const err = await response.json();
                          toast.error(err.detail || 'Failed to reassign');
                        }
                      } catch (err) {
                        console.error('Error reassigning:', err);
                        toast.error('Failed to reassign customers');
                      }
                    }}
                    className="w-full p-3 text-left bg-zinc-50 hover:bg-zinc-100 rounded-lg transition-colors flex items-center gap-3"
                  >
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                      <User size={20} className="text-purple-600" />
                    </div>
                    <span className="font-medium">{worker}</span>
                  </button>
                ))}
              </div>
              
              <div className="pt-4 border-t">
                <p className="text-sm font-medium text-zinc-700 mb-2">Apply to:</p>
                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="reassign-scope" value="all" defaultChecked className="text-emerald-500" />
                    <span className="text-sm">All jobs (existing + future)</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="reassign-scope" value="once" className="text-emerald-500" />
                    <span className="text-sm">Customer only (no job changes)</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Bulk Move to Round Modal */}
      {showBulkMoveRound && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowBulkMoveRound(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold text-lg">Move {selectedCustomerIds.size} Customers to Round</h3>
              <button onClick={() => setShowBulkMoveRound(false)} className="p-2 hover:bg-zinc-100 rounded-lg" data-testid="close-move-round-modal">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <p className="text-sm text-zinc-600">Select a round or create a new one:</p>
              
              {/* New Round Input */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newRoundName}
                  onChange={(e) => setNewRoundName(e.target.value)}
                  placeholder="Enter new round name..."
                  className="flex-1 px-3 py-2 border border-zinc-200 rounded-lg focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                  data-testid="new-round-input"
                />
                <button
                  onClick={async () => {
                    if (!newRoundName.trim()) return;
                    try {
                      const token = api.getToken();
                      // Create the new round first
                      const createRes = await fetch(`${API_URL}/api/rounds`, {
                        method: 'POST',
                        headers: {
                          'Authorization': `Bearer ${token}`,
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({ name: newRoundName.trim() })
                      });
                      
                      if (!createRes.ok && createRes.status !== 400) {
                        throw new Error('Failed to create round');
                      }
                      
                      // Now update customers with this round
                      const customerIds = Array.from(selectedCustomerIds);
                      const response = await fetch(`${API_URL}/api/bulk/customers-round`, {
                        method: 'PUT',
                        headers: {
                          'Authorization': `Bearer ${token}`,
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                          customer_ids: customerIds,
                          round: newRoundName.trim()
                        })
                      });
                      
                      if (response.ok) {
                        const result = await response.json();
                        toast.success(`Moved ${result.customers_updated} customers to "${newRoundName.trim()}"`);
                        setShowBulkMoveRound(false);
                        setNewRoundName('');
                        clearSelection();
                        refreshCustomers();
                        // Add to local rounds list if not already there
                        if (!roundsList.includes(newRoundName.trim())) {
                          setRoundsList(prev => [...prev, newRoundName.trim()].sort());
                        }
                        // Notify Work Planner / Visual Planner / Schedule
                        // so round badges refresh immediately.
                        customerChanged(Actions.BULK_UPDATE, { customer_ids: customerIds, round: newRoundName.trim() });
                      } else {
                        const err = await response.json();
                        toast.error(err.detail || 'Failed to move customers');
                      }
                    } catch (err) {
                      console.error('Error moving to round:', err);
                      toast.error('Failed to move customers to round');
                    }
                  }}
                  disabled={!newRoundName.trim()}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-300 text-white rounded-lg font-medium transition-colors"
                  data-testid="create-round-btn"
                >
                  Create & Move
                </button>
              </div>
              
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-zinc-200" />
                </div>
                <div className="relative flex justify-center">
                  <span className="px-2 bg-white text-sm text-zinc-500">or select existing</span>
                </div>
              </div>
              
              {/* Existing Rounds List */}
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {roundsList.length === 0 ? (
                  <p className="text-sm text-zinc-500 text-center py-4">No rounds yet. Create one above.</p>
                ) : (
                  roundsList.map(round => (
                    <button
                      key={round}
                      onClick={async () => {
                        try {
                          const token = api.getToken();
                          const customerIds = Array.from(selectedCustomerIds);
                          const response = await fetch(`${API_URL}/api/bulk/customers-round`, {
                            method: 'PUT',
                            headers: {
                              'Authorization': `Bearer ${token}`,
                              'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({
                              customer_ids: customerIds,
                              round: round
                            })
                          });
                          
                          if (response.ok) {
                            const result = await response.json();
                            toast.success(`Moved ${result.customers_updated} customers to "${round}"`);
                            setShowBulkMoveRound(false);
                            clearSelection();
                            refreshCustomers();
                            customerChanged(Actions.BULK_UPDATE, { customer_ids: customerIds, round: round });
                          } else {
                            const err = await response.json();
                            toast.error(err.detail || 'Failed to move customers');
                          }
                        } catch (err) {
                          console.error('Error moving to round:', err);
                          toast.error('Failed to move customers to round');
                        }
                      }}
                      className="w-full p-3 text-left bg-zinc-50 hover:bg-zinc-100 rounded-lg transition-colors flex items-center gap-3"
                      data-testid={`round-option-${round}`}
                    >
                      <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                        <MapPin size={20} className="text-amber-600" />
                      </div>
                      <span className="font-medium">{round}</span>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Bulk Message Modal */}
      {showBulkMessage && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowBulkMessage(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold text-lg">Message {selectedCustomerIds.size} Customers</h3>
              <button onClick={() => setShowBulkMessage(false)} className="p-2 hover:bg-zinc-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="text-sm font-medium text-zinc-700 mb-1 block">Message</label>
                <textarea
                  id="bulk-message-text"
                  placeholder="Type your message here..."
                  className="w-full p-3 border border-zinc-200 rounded-lg min-h-[120px] focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                />
              </div>
              
              <div>
                <p className="text-sm font-medium text-zinc-700 mb-2">Send via:</p>
                <div className="flex gap-2">
                  <label className="flex items-center gap-2 px-3 py-2 bg-zinc-50 rounded-lg cursor-pointer hover:bg-zinc-100">
                    <input type="checkbox" id="send-sms" defaultChecked className="rounded text-emerald-500" />
                    <MessageSquare size={16} className="text-emerald-600" />
                    <span className="text-sm">SMS</span>
                  </label>
                  <label className="flex items-center gap-2 px-3 py-2 bg-zinc-50 rounded-lg cursor-pointer hover:bg-zinc-100">
                    <input type="checkbox" id="send-email" defaultChecked className="rounded text-emerald-500" />
                    <Mail size={16} className="text-blue-600" />
                    <span className="text-sm">Email</span>
                  </label>
                </div>
              </div>
              
              <div className="bg-zinc-50 rounded-lg p-3">
                <p className="text-xs text-zinc-500 mb-1">Recipients with contact info:</p>
                <div className="flex gap-4 text-sm">
                  <span><strong>{selectedCustomers.filter(c => c.phone).length}</strong> with phone</span>
                  <span><strong>{selectedCustomers.filter(c => c.email).length}</strong> with email</span>
                </div>
              </div>
              
              <button
                onClick={async () => {
                  const message = document.getElementById('bulk-message-text').value;
                  const sendSms = document.getElementById('send-sms').checked;
                  const sendEmail = document.getElementById('send-email').checked;
                  
                  if (!message.trim()) {
                    toast.error('Please enter a message');
                    return;
                  }
                  
                  if (!sendSms && !sendEmail) {
                    toast.error('Please select at least one delivery method');
                    return;
                  }
                  
                  try {
                    const token = api.getToken();
                    let smsCount = 0;
                    let emailCount = 0;
                    const failures = [];

                    for (const customer of selectedCustomers) {
                      if (sendSms && customer.phone) {
                        const r = await fetch(`${API_URL}/api/sms/send`, {
                          method: 'POST',
                          headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                          },
                          body: JSON.stringify({
                            recipient_phone: customer.phone,
                            message: message,
                            customer_id: customer.id
                          })
                        });
                        if (r.ok) {
                          smsCount++;
                        } else {
                          let why = `${r.status}`;
                          try { const j = await r.clone().json(); why = j.detail || why; } catch { /* ignore */ }
                          failures.push(`SMS to ${customer.name}: ${why}`);
                        }
                      }

                      if (sendEmail && customer.email) {
                        const r = await fetch(`${API_URL}/api/customers/send-email`, {
                          method: 'POST',
                          headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                          },
                          body: JSON.stringify({
                            customer_id: customer.id,
                            recipient_email: customer.email,
                            subject: 'Message from Perfect Panes',
                            body: message
                          })
                        });
                        if (r.ok) {
                          emailCount++;
                        } else {
                          let why = `${r.status}`;
                          try { const j = await r.clone().json(); why = j.detail || why; } catch { /* ignore */ }
                          failures.push(`Email to ${customer.name}: ${why}`);
                        }
                      }
                    }

                    if (failures.length) {
                      toast.error(`Sent ${smsCount} SMS and ${emailCount} emails. ${failures.length} failed.`);
                      console.error('Bulk message failures:', failures);
                    } else {
                      toast.success(`Sent ${smsCount} SMS and ${emailCount} emails`);
                    }
                    setShowBulkMessage(false);
                    clearSelection();
                  } catch (err) {
                    console.error('Error sending messages:', err);
                    toast.error('Failed to send some messages');
                  }
                }}
                className="w-full py-3 bg-emerald-500 text-white rounded-lg font-medium hover:bg-emerald-600 transition-colors flex items-center justify-center gap-2"
              >
                <Send size={18} />
                Send Messages
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Customers;
